/**
 * Centralized error keys for i18n translation.
 * Organized by module for better maintainability.
 *
 * Format: errors.{module}.{error_type}
 *
 * @example
 * throw new I18nException({
 *   key: ERROR_KEYS.USERS.NOT_FOUND,
 *   status: HttpStatus.NOT_FOUND,
 *   params: { userId: '123' }
 * });
 */
export const ERROR_KEYS = {
    PERSONNEL_COSTS: {
        INVALID: 'errors.personnel_costs.invalid',
        EMPLOYEE_NOT_FOUND: 'errors.personnel_costs.employee_not_found',
        IDENTITY_LOCKED: 'errors.personnel_costs.identity_locked',
        TECHNICIAN_UNAVAILABLE: 'errors.personnel_costs.technician_unavailable',
        CHANGED: 'errors.personnel_costs.changed',
        ACTUAL_TO_ESTIMATE: 'errors.personnel_costs.actual_to_estimate',
        SOURCE_REQUIRED: 'errors.personnel_costs.source_required',
        SESSION_CHANGED: 'errors.personnel_costs.session_changed',
        REFERENCE_HOURS: 'errors.personnel_costs.reference_hours',
        COMMESSA_NOT_FOUND: 'errors.personnel_costs.commessa_not_found',
        CONFIRMATION_REQUIRED: 'errors.personnel_costs.confirmation_required',
        NOT_FOUND: 'errors.personnel_costs.not_found',
        F24_NOT_FOUND: 'errors.personnel_costs.f24_not_found',
        F24_ALREADY_LINKED: 'errors.personnel_costs.f24_already_linked',
        SUPPLEMENT_REQUIRES_WEIGHTS: 'errors.personnel_costs.supplement_requires_weights',
    },
    // Generic errors
    GENERIC: 'errors.generic',
    NOT_FOUND: 'errors.not_found',
    FORBIDDEN: 'errors.forbidden',
    UNAUTHORIZED: 'errors.unauthorized',
    INTERNAL_SERVER_ERROR: 'errors.internal_server_error',
    /**
     * Limite di richieste superato (throttler per utente e tenant, es. email ai
     * clienti): contano anche i tentativi falliti.
     */
    TOO_MANY_REQUESTS: 'errors.too_many_requests',
    /**
     * Database momentaneamente non disponibile (connessione persa, pool saturo,
     * istanza in sola lettura): il filtro globale risponde 503 con
     * `Retry-After`, il client può riprovare.
     */
    DB_UNAVAILABLE: 'errors.db_unavailable',
    /**
     * errorCode per il logging persistito: violazione di unicità (P2002) non
     * intercettata dal service, arrivata al filtro globale come 409. È una
     * corsa da correggere alla fonte.
     */
    UNIQUE_CONSTRAINT_RACE: 'errors.unique_constraint_race',
    /**
     * Fallimento di un servizio esterno senza una chiave dedicata: è il default
     * del client HTTP condiviso (`common/http`), usato dai provider che non
     * personalizzano il mapping degli errori.
     */
    EXTERNAL_SERVICE: {
        UPSTREAM_ERROR: 'errors.external_service.upstream_error',
    },
    REDIS: {
        /**
         * errorCode per il logging persistito: Redis instabile oltre la grazia di
         * `RedisService` (una riga per interruzione, mai per singolo evento di
         * ioredis). Non arriva all'utente.
         */
        CONNECTION_LOST: 'errors.redis.connection_lost',
    },
    // Cambi valuta (BCE)
    EXCHANGE_RATES: {
        RATE_UNAVAILABLE: 'errors.exchange_rates.rate_unavailable',
        UPSTREAM_ERROR: 'errors.exchange_rates.upstream_error',
        SYNC_FAILED: 'errors.exchange_rates.sync_failed',
    },
    // Auth module
    AUTH: {
        INVALID_CREDENTIALS: 'errors.auth.invalid_credentials',
        TOKEN_EXPIRED: 'errors.auth.token_expired',
        TOKEN_INVALID: 'errors.auth.token_invalid',
        EMAIL_NOT_VERIFIED: 'errors.auth.email_not_verified',
        DISPOSABLE_EMAIL: 'errors.auth.disposable_email',
        PASSWORD_TOO_SHORT: 'errors.auth.password_too_short',
        PASSWORDS_DO_NOT_MATCH: 'errors.auth.passwords_do_not_match',
        REFRESH_TOKEN_INVALID: 'errors.auth.refresh_token_invalid',
        REFRESH_TOKEN_EXPIRED: 'errors.auth.refresh_token_expired',
        OPERATION_BLOCKED_DURING_IMPERSONATION: 'errors.auth.operation_blocked_during_impersonation',
        DEVICE_RECENT_LOGIN_REQUIRED: 'errors.auth.device_recent_login_required',
        DEVICE_INVALID: 'errors.auth.device_invalid',
        DEVICE_REVOKED: 'errors.auth.device_revoked',
        DEVICE_SECRET_CONFLICT: 'errors.auth.device_secret_conflict',
        WEB_LOGIN_NO_DEVICE: 'errors.auth.web_login_no_device',
        WEB_LOGIN_NOT_FOUND: 'errors.auth.web_login_not_found',
        WEB_LOGIN_NOT_APPROVED: 'errors.auth.web_login_not_approved',
        WEB_LOGIN_ALREADY_DECIDED: 'errors.auth.web_login_already_decided',
        PASSKEY_RECENT_LOGIN_REQUIRED: 'errors.auth.passkey_recent_login_required',
        PASSKEY_INVALID: 'errors.auth.passkey_invalid',
        PASSKEY_UNKNOWN: 'errors.auth.passkey_unknown',
        PASSKEY_CHALLENGE_EXPIRED: 'errors.auth.passkey_challenge_expired',
        CREDENTIAL_REVOCATION_FAILED: 'errors.auth.credential_revocation_failed',
        PASSWORD_UNCHANGED: 'errors.auth.password_unchanged',
        SESSION_NOT_FOUND: 'errors.auth.session_not_found',
        SESSION_IS_CURRENT: 'errors.auth.session_is_current',
        /**
         * errorCode di log: la seconda passata di revoche dopo un cambio password
         * riuscito è fallita (la password è comunque cambiata).
         */
        CHANGE_PASSWORD_REVOKE_FAILED: 'errors.auth.change_password_revoke_failed',
        /**
         * errorCode di log (warn): password attuale sbagliata nel cambio password
         * da autenticato. Serve a contare i tentativi su una sessione rubata.
         */
        CHANGE_PASSWORD_INVALID_CURRENT: 'errors.auth.change_password_invalid_current',
    },
    // Users module
    USERS: {
        NOT_FOUND: 'errors.users.not_found',
        EMAIL_ALREADY_EXISTS: 'errors.users.email_already_exists',
        ALREADY_MEMBER: 'errors.users.already_member',
        NOT_MEMBER: 'errors.users.not_member',
        CANNOT_DELETE_ONLY_OWNER: 'errors.users.cannot_delete_only_owner',
        CANNOT_DELETE_ALL_OWNERS: 'errors.users.cannot_delete_all_owners',
        VERIFICATION_TOKEN_INVALID: 'errors.users.verification_token_invalid',
        VERIFICATION_TOKEN_USED: 'errors.users.verification_token_used',
        VERIFICATION_TOKEN_EXPIRED: 'errors.users.verification_token_expired',
        EMAIL_ALREADY_VERIFIED: 'errors.users.email_already_verified',
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.users.export_format_not_supported',
        NAME_MANAGED_ELSEWHERE: 'errors.users.name_managed_elsewhere',
    },
    // Tenants module
    TENANTS: {
        NOT_FOUND: 'errors.tenants.not_found',
        VAT_ALREADY_EXISTS: 'errors.tenants.vat_already_exists',
        FISCAL_CODE_ALREADY_EXISTS: 'errors.tenants.fiscal_code_already_exists',
        AT_LEAST_ONE_TAX_FIELD_REQUIRED: 'errors.tenants.at_least_one_tax_field_required',
        USER_ALREADY_EXISTS: 'errors.tenants.user_already_exists',
        USER_ALREADY_MEMBER: 'errors.tenants.user_already_member',
        USER_ALREADY_OWNS_TENANT: 'errors.tenants.user_already_owns_tenant',
        USER_NOT_FOUND: 'errors.tenants.user_not_found',
        OWNER_NOT_FOUND: 'errors.tenants.owner_not_found',
        INVITATION_NOT_FOUND: 'errors.tenants.invitation_not_found',
        INVITATION_INVALID: 'errors.tenants.invitation_invalid',
        INVITATION_EXPIRED: 'errors.tenants.invitation_expired',
        INVITATION_ALREADY_SENT: 'errors.tenants.invitation_already_sent',
        INVITATION_ALREADY_ACCEPTED: 'errors.tenants.invitation_already_accepted',
        NEW_USER_MISSING_CREDENTIALS: 'errors.tenants.new_user_missing_credentials',
        CANNOT_REMOVE_OWNER: 'errors.tenants.cannot_remove_owner',
        CANNOT_CHANGE_OWNER_ROLE: 'errors.tenants.cannot_change_owner_role',
        ACCESS_DENIED: 'errors.tenants.access_denied',
        NO_ROLE_FOUND: 'errors.tenants.no_role_found',
        INSUFFICIENT_PERMISSIONS: 'errors.tenants.insufficient_permissions',
        TENANT_NOT_SPECIFIED: 'errors.tenants.tenant_not_specified',
        TENANT_NOT_ACTIVE: 'errors.tenants.tenant_not_active',
        ACTIVE_SUBSCRIPTION_EXISTS: 'errors.tenants.active_subscription_exists',
        NO_ACTIVE_PLAN: 'errors.tenants.no_active_plan',
        VAT_MISMATCH: 'errors.tenants.vat_mismatch',
        // Codice fiscale diverso da quello del tenant già collegato al contratto: 400.
        FISCAL_CODE_MISMATCH: 'errors.tenants.fiscal_code_mismatch',
        INVALID_VAT_ID: 'errors.tenants.invalid_vat_id',
        ADDITIONAL_VAT_ID_ALREADY_ADDED: 'errors.tenants.additional_vat_id_already_added',
        ADDITIONAL_VAT_ID_EQUALS_PRIMARY: 'errors.tenants.additional_vat_id_equals_primary',
        ADDITIONAL_VAT_ID_IN_USE: 'errors.tenants.additional_vat_id_in_use',
        ADDITIONAL_VAT_ID_NOT_FOUND: 'errors.tenants.additional_vat_id_not_found',
        INVALID_FISCAL_CODE: 'errors.tenants.invalid_fiscal_code',
        TAX_SUBJECT_TYPE_REQUIRED: 'errors.tenants.tax_subject_type_required',
        PERSONAL_FISCAL_CODE_REQUIRED: 'errors.tenants.personal_fiscal_code_required',
        ENTITY_FISCAL_CODE_REQUIRED: 'errors.tenants.entity_fiscal_code_required',
        TAX_SUBJECT_REGIME_MISMATCH: 'errors.tenants.tax_subject_regime_mismatch',
        IMPERSONATION_TENANT_MISMATCH: 'errors.tenants.impersonation_tenant_mismatch',
        ONE_OWNER_REQUIRED: 'errors.tenants.one_owner_required',
        CANNOT_ASSIGN_HIGHER_ROLE: 'errors.tenants.cannot_assign_higher_role',
        PERMISSION_DEPENDENCY_VIOLATION: 'errors.tenants.permission_dependency_violation',
        CANNOT_GRANT_UNOWNED_PERMISSION: 'errors.tenants.cannot_grant_unowned_permission',
        DEMO_EXPIRED: 'errors.tenants.demo_expired',
        SUBSCRIPTION_ENDED: 'errors.tenants.subscription_ended',
        SUBSCRIPTION_UNPAID: 'errors.tenants.subscription_unpaid',
        DELETION_NOT_SCHEDULED: 'errors.tenants.deletion_not_scheduled',
        DELETION_NOT_REVERSIBLE: 'errors.tenants.deletion_not_reversible',
    },
    // Anteprima permessi (OWNER/ADMIN vedono la webapp come un altro membro)
    PERMISSION_PREVIEW: {
        READ_ONLY: 'errors.permission_preview.read_only',
        ENDED: 'errors.permission_preview.ended',
        TENANT_MISMATCH: 'errors.permission_preview.tenant_mismatch',
        ALREADY_ACTIVE: 'errors.permission_preview.already_active',
        SELF_NOT_ALLOWED: 'errors.permission_preview.self_not_allowed',
        TARGET_NOT_ALLOWED: 'errors.permission_preview.target_not_allowed',
        NOT_ALLOWED_DURING_IMPERSONATION: 'errors.permission_preview.not_allowed_during_impersonation',
    },
    // Advisor module (Pannello Commercialista)
    ADVISOR: {
        PROFILE_ALREADY_EXISTS: 'errors.advisor.profile_already_exists',
        PROFILE_NOT_FOUND: 'errors.advisor.profile_not_found',
        NOT_ADVISOR_FOR_TENANT: 'errors.advisor.not_advisor_for_tenant',
    },
    // Utenti backoffice (admin)
    ADMINS: {
        NOT_FOUND: 'errors.admins.not_found',
        ALREADY_ADMIN: 'errors.admins.already_admin',
        CANNOT_REVOKE_SELF: 'errors.admins.cannot_revoke_self',
        CANNOT_REVOKE_LAST: 'errors.admins.cannot_revoke_last',
        CANNOT_CHANGE_SELF_ROLE: 'errors.admins.cannot_change_self_role',
        SETUP_EMAIL_FAILED: 'errors.admins.setup_email_failed',
    },
    // Contracts module (firma contratto in-house SES)
    CONTRACTS: {
        NOT_FOUND: 'errors.contracts.not_found',
        INVALID_TOKEN: 'errors.contracts.invalid_token',
        OTP_INVALID: 'errors.contracts.otp_invalid',
        OTP_EXPIRED: 'errors.contracts.otp_expired',
        OTP_MAX_ATTEMPTS: 'errors.contracts.otp_max_attempts',
        OTP_RATE_LIMITED: 'errors.contracts.otp_rate_limited',
        ALREADY_SIGNED: 'errors.contracts.already_signed',
        NOT_SENDABLE: 'errors.contracts.not_sendable',
        NOT_SHAREABLE: 'errors.contracts.not_shareable',
        // Codice di log (non mostrato all'utente): copia inviata, evento non scritto.
        SHARE_EVENT_NOT_RECORDED: 'errors.contracts.share_event_not_recorded',
        // Codice di log (non mostrato all'utente): la variazione programmata non
        // si è attivata, resta in vigore il contratto corrente.
        SCHEDULED_ACTIVATION_FAILED: 'errors.contracts.scheduled_activation_failed',
        INCOMPLETE_DRAFT: 'errors.contracts.incomplete_draft',
        NOT_CANCELABLE: 'errors.contracts.not_cancelable',
        NOT_REOPENABLE: 'errors.contracts.not_reopenable',
        NOT_EDITABLE: 'errors.contracts.not_editable',
        PDF_NOT_AVAILABLE: 'errors.contracts.pdf_not_available',
        INVALID_PDF: 'errors.contracts.invalid_pdf',
        TERMS_NOT_ACCEPTED: 'errors.contracts.terms_not_accepted',
        DATA_NOT_CONFIRMED: 'errors.contracts.data_not_confirmed',
        // Demo (Contract type = DEMO)
        DEV_DEMO_NOT_CONFIGURED: 'errors.contracts.dev_demo_not_configured',
        DEV_DEMO_UNAVAILABLE: 'errors.contracts.dev_demo_unavailable',
        DEV_DEMO_ONLY: 'errors.contracts.dev_demo_only',
        DEV_DEMO_IN_PROGRESS: 'errors.contracts.dev_demo_in_progress',
        NOT_A_DEMO: 'errors.contracts.not_a_demo',
        DEMO_NOT_EXTENDABLE: 'errors.contracts.demo_not_extendable',
        INVALID_DEMO_EXPIRY: 'errors.contracts.invalid_demo_expiry',
        INVALID_PAYMENT_TERMS: 'errors.contracts.invalid_payment_terms',
        INVALID_COMMERCIAL_OPTIONS: 'errors.contracts.invalid_commercial_options',
        // Sconto con rivenditore assente o non AGENT/RESELLER: 400.
        INVALID_DISCOUNT: 'errors.contracts.invalid_discount',
        RECIPIENT_DISTINCT_VAT: 'errors.contracts.recipient_distinct_vat',
        RECIPIENT_CAPACITY: 'errors.contracts.recipient_capacity',
        RECIPIENT_ADVISOR_REQUIRED: 'errors.contracts.recipient_advisor_required',
        INVALID_AMENDMENT_PARENT: 'errors.contracts.invalid_amendment_parent',
        AMENDMENT_ALREADY_PENDING: 'errors.contracts.amendment_already_pending',
        TENANT_NOT_DEMO: 'errors.contracts.tenant_not_demo',
        TENANT_ALREADY_CONTRACTED: 'errors.contracts.tenant_already_contracted',
        // Intestatario rivenditore senza rivenditore o compenso, o campi del
        // rivenditore inviati con intestatario cliente: 400.
        BILLING_PARTY_MISMATCH: 'errors.contracts.billing_party_mismatch',
        // Il rivenditore intestatario non è un RESELLER attivo raggiungibile: 400.
        BILLING_REFERRER_INVALID: 'errors.contracts.billing_referrer_invalid',
        // Variazione pagata dal rivenditore: il cliente (P.IVA, C.F., accesso
        // come commercialista) è quello del contratto variato: 400.
        AMENDMENT_RECIPIENT_LOCKED: 'errors.contracts.amendment_recipient_locked',
        // Fine servizio immediata di un contratto che non è (ancora) quello in
        // vigore, es. attivazione del cliente del rivenditore in sospeso: 409.
        TERMINATION_NOT_IN_FORCE: 'errors.contracts.termination_not_in_force',
        // Codici di log (non mostrati): attivazione del cliente del rivenditore.
        RESELLER_CLIENT_ACTIVATION_ABANDONED: 'CONTRACT_RESELLER_CLIENT_ACTIVATION_ABANDONED',
        RESELLER_CLIENT_ACTIVATION_RETRY_FAILED: 'CONTRACT_RESELLER_CLIENT_ACTIVATION_RETRY_FAILED',
    },
    COMMERCIAL_LIMITS: {
        EMAIL_REVIEW_REQUIRED: 'errors.commercial_limits.email_review_required',
        EXCEEDED: 'errors.commercial_limits.exceeded',
        WAREHOUSE_LIMIT_REACHED: 'errors.commercial_limits.warehouse_limit_reached',
        MODULE_NOT_INCLUDED: 'errors.commercial_limits.module_not_included',
        DOCUMENTS_SUSPENDED: 'errors.commercial_limits.documents_suspended',
    },
    // Prenotazioni sui contatori commerciali del gruppo che pagano insieme
    TENANT_GROUP: {
        CONTRACT_CHANGED: 'errors.tenant_group.contract_changed',
        USAGE_CHANGED: 'errors.tenant_group.usage_changed',
    },
    // Intent declarations module (dichiarazioni d'intento ricevute)
    INTENT_DECLARATIONS: {
        NOT_FOUND: 'errors.intent_declarations.not_found',
        INVALID_PROTOCOL: 'errors.intent_declarations.invalid_protocol',
        DUPLICATE_PROTOCOL: 'errors.intent_declarations.duplicate_protocol',
        IN_USE: 'errors.intent_declarations.in_use',
        COMPANY_MISMATCH: 'errors.intent_declarations.company_mismatch',
        NOT_ACTIVE: 'errors.intent_declarations.not_active',
        YEAR_MISMATCH: 'errors.intent_declarations.year_mismatch',
        NOT_APPLICABLE: 'errors.intent_declarations.not_applicable',
        IDENTITY_LOCKED: 'errors.intent_declarations.identity_locked',
        COMPANY_REQUIRED: 'errors.intent_declarations.company_required',
        DATE_BEFORE_PROTOCOL: 'errors.intent_declarations.date_before_protocol',
        INVALID_PROTOCOL_DATE: 'errors.intent_declarations.invalid_protocol_date',
        PREVIOUS_INVALID: 'errors.intent_declarations.previous_invalid',
        SUPERSEDED_LOCKED: 'errors.intent_declarations.superseded_locked',
    },
    // Companies module
    COMPANY_PA_REFERENCES: {
        NOT_FOUND: 'errors.company_pa_references.not_found',
        INVALID_PERIOD: 'errors.company_pa_references.invalid_period',
        INVALID_COMPANY: 'errors.company_pa_references.invalid_company',
    },
    COMPANIES: {
        NOT_FOUND: 'errors.companies.not_found',
        VAT_NUMBER_ALREADY_EXISTS: 'errors.companies.vat_number_already_exists',
        INVALID_VAT_NUMBER: 'errors.companies.invalid_vat_number',
        INVALID_FISCAL_CODE: 'errors.companies.invalid_fiscal_code',
        RECORD_IN_USE: 'errors.companies.record_in_use',
        MULTIPLE_DEFAULT_BANK_ACCOUNTS: 'errors.companies.multiple_default_bank_accounts',
        DUPLICATE_BANK_ACCOUNT_IBAN: 'errors.companies.duplicate_bank_account_iban',
        NO_DEFAULT_BANK_ACCOUNT: 'errors.companies.no_default_bank_account',
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.companies.export_format_not_supported',
        VIES_UNAVAILABLE: 'errors.companies.vies_unavailable',
        VIES_INVALID_INPUT: 'errors.companies.vies_invalid_input',
        COMMERCIAL_PRODUCT_KEY_INVALID: 'errors.companies.commercial_product_key_invalid',
        COMMERCIAL_EXPORT_TOO_LARGE: 'errors.companies.commercial_export_too_large',
        MATERIAL_NOT_FOUND: 'errors.companies.material_not_found',
        MATERIAL_RETURN_BEFORE_DELIVERY: 'errors.companies.material_return_before_delivery',
        MATERIAL_RETURN_NOT_FOUND: 'errors.companies.material_return_not_found',
        MATERIAL_RETURN_EXCEEDS_RESIDUAL: 'errors.companies.material_return_exceeds_residual',
        MATERIAL_HAS_RETURNS: 'errors.companies.material_has_returns',
        MATERIAL_DOCUMENT_INVALID: 'errors.companies.material_document_invalid',
        MATERIAL_PRODUCT_INVALID: 'errors.companies.material_product_invalid',
    },
    RECURRING_INVOICES: {
        NOT_FOUND: 'errors.recurring_invoices.not_found',
    },
    // Documents module
    DOCUMENT_ADVANCES: {
        REFERENCE_MISSING: 'errors.document_advances.reference_missing',
        REFERENCE_DESCRIPTION_SPACE: 'errors.document_advances.reference_description_space',
        LINKED_DOCUMENT_LOCKED: 'errors.document_advances.linked_document_locked',
        INVALID_APPLICATION: 'errors.document_advances.invalid_application',
    },
    DOCUMENTS: {
        DEBIT_ACCOUNT_NOT_FOUND: 'errors.documents.debit_account_not_found',
        XML_EXPORT_EMPTY: 'errors.documents.xml_export_empty',
        EMAIL_XML_NOT_AVAILABLE: 'errors.documents.email_xml_not_available',
        XML_EXPORT_TOO_LARGE: 'errors.documents.xml_export_too_large',
        OPERATION_CONFLICT: 'errors.documents.operation_conflict',
        NOT_FOUND: 'errors.documents.not_found',
        SEQUENCE_SORT_NOT_FIRST: 'errors.documents.sequence_sort_not_first',
        SEQUENCE_SORT_UNSUPPORTED: 'errors.documents.sequence_sort_unsupported',
        SEQUENCE_SORT_INVALID_PAGINATION: 'errors.documents.sequence_sort_invalid_pagination',
        INCOMING_REVIEW_INVALID_TRANSITION: 'errors.documents.incoming_review_invalid_transition',
        INVALID_AMOUNT_RANGE: 'errors.documents.invalid_amount_range',
        INVALID_STATUS: 'errors.documents.invalid_status',
        ALREADY_PAID: 'errors.documents.already_paid',
        CANNOT_DELETE_WITH_PAYMENTS: 'errors.documents.cannot_delete_with_payments',
        INVALID_AMOUNT: 'errors.documents.invalid_amount',
        INVALID_DATE: 'errors.documents.invalid_date',
        MISSING_COMPANY: 'errors.documents.missing_company',
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.documents.export_format_not_supported',
        DUPLICATE: 'errors.documents.duplicate',
        MISSING_NUMBER: 'errors.documents.missing_number',
        CANNOT_DELETE_WITH_RECONCILIATION_LINKS: 'errors.documents.cannot_delete_with_reconciliation_links',
        CANNOT_DELETE_EXTERNAL_DOCUMENT: 'errors.documents.cannot_delete_external_document',
        CANNOT_DELETE_WITH_LOCKED_CHILDREN: 'errors.documents.cannot_delete_with_locked_children',
        /** Un DDT citato da una fattura (singola o riepilogativa TD24). */
        CANNOT_DELETE_REFERENCED_BY_INVOICE: 'errors.documents.cannot_delete_referenced_by_invoice',
        /** Una fattura da cui è già stato generato un DDT di consegna. */
        CANNOT_DELETE_WITH_DERIVED_DELIVERY_NOTE: 'errors.documents.cannot_delete_with_derived_delivery_note',
        /** Ripristino che porterebbe il padre oltre la quantità ordinata. */
        CANNOT_RESTORE_QUANTITY_EXCEEDED: 'errors.documents.cannot_restore_quantity_exceeded',
        DOWNLOAD_JOB_ALREADY_SCHEDULED: 'errors.documents.download_job_already_scheduled',
        GROUP_INVOICE_MIXED_COMPANIES: 'errors.documents.group_invoice_mixed_companies',
        GROUP_INVOICE_NOT_DDT: 'errors.documents.group_invoice_not_ddt',
        GROUP_INVOICE_NOT_OUTGOING: 'errors.documents.group_invoice_not_outgoing',
        GROUP_INVOICE_NOT_SHIPPED: 'errors.documents.group_invoice_not_shipped',
        GROUP_INVOICE_INVALID_STATUS: 'errors.documents.group_invoice_invalid_status',
        GROUP_INVOICE_DRAFT_REQUIRES_CONFIRMATION: 'errors.documents.group_invoice_draft_requires_confirmation',
        TD24_MISSING_DDT_REFERENCE: 'errors.documents.td24_missing_ddt_reference',
        EMAIL_NOT_SENDABLE: 'errors.documents.email_not_sendable',
        // Sollecito manuale (`send-email` con `paymentReminder`): solo fatture di
        // vendita con rate ancora da incassare. ARRIVA all'utente.
        PAYMENT_REMINDER_NOT_APPLICABLE: 'errors.documents.payment_reminder_not_applicable',
        TD24_MISSING_DDT_NUMBER: 'errors.documents.td24_missing_ddt_number',
        TD24_MISSING_DDT_DATE: 'errors.documents.td24_missing_ddt_date',
        // Versamento CERTIFICATO dal cassetto fiscale (un `TAX_PAYMENT` con almeno
        // un `AcubeF24Form` agganciato): l'Agenzia documenta importo, data, numero
        // e scadenze, quindi quei campi sono in sola lettura e la cancellazione è
        // bloccata (l'evidenza resterebbe orfana). Categoria, descrizione, note e
        // commessa restano modificabili. ARRIVA all'utente.
        // Documento F24 nato dalla conferma di una delega ancora viva: importo,
        // data e scadenza si correggono dalla delega («Rivedi»)
        F24_DELEGATION_AMOUNTS_LOCKED: 'errors.documents.f24_delegation_amounts_locked',
        // Stesso documento con la delega già consolidata, inviata o pagata: su
        // CGN non si corregge più, importo e data sono quelli del versamento
        F24_DELEGATION_AMOUNTS_LOCKED_DISPATCHED: 'errors.documents.f24_delegation_amounts_locked_dispatched',
        CERTIFIED_BY_TAX_DRAWER_LOCKED: 'errors.documents.certified_by_tax_drawer_locked',
        STAMP_DUTY_CHARGE_VAT_RATE_INVALID: 'errors.documents.stamp_duty_charge_vat_rate_invalid',
        // `DatiRitenuta/CausalePagamento` è obbligatoria nel tracciato FatturaPA
        // e non è deducibile: il mapper si rifiuta di inventarla. ARRIVA
        // all'utente (il pre-check la segnala prima, questa è la rete).
        WITHHOLDING_CAUSALE_MISSING: 'errors.documents.withholding_causale_missing',
        // Fattura presa in carico dal servizio di fatturazione, ma i movimenti di
        // magazzino non sono stati registrati: la trasmissione resta valida e il
        // documento porta l'avviso. NON arriva all'utente come errore HTTP —
        // serve a dare una riga persistente in `error_log` all'incidente.
        STOCK_POSTING_AFTER_SDI_FAILED: 'errors.documents.stock_posting_after_sdi_failed',
        // Integrazione TD16 (reverse charge interno). ARRIVANO tutte all'utente:
        // nascono dall'azione "Crea integrazione" sul dettaglio di una fattura
        // di acquisto.
        /** Il documento da integrare non è una fattura di ACQUISTO. */
        RC_INTEGRATION_SOURCE_NOT_PURCHASE_INVOICE: 'errors.documents.rc_integration_source_not_purchase_invoice',
        /** Nessuna riga con natura N6.x: non c'è nulla da integrare. */
        RC_INTEGRATION_NO_REVERSE_CHARGE_LINES: 'errors.documents.rc_integration_no_reverse_charge_lines',
        /** Esiste già un'integrazione viva per questa fattura passiva. */
        RC_INTEGRATION_ALREADY_EXISTS: 'errors.documents.rc_integration_already_exists',
        /** L'aliquota da integrare non è nell'anagrafica aliquote del tenant. */
        RC_INTEGRATION_VAT_RATE_UNRESOLVED: 'errors.documents.rc_integration_vat_rate_unresolved',
        /** Aliquota di integrazione non ammessa (deve essere > 0). */
        RC_INTEGRATION_VAT_RATE_INVALID: 'errors.documents.rc_integration_vat_rate_invalid',
        // Documenti a parti invertite composti da zero (TD28 San Marino con IVA,
        // TD29 comunicazione di omessa o irregolare fatturazione). ARRIVANO
        // tutte all'utente: nascono dai due dialog della lista fatture.
        /** Il fornitore indicato non esiste nel tenant. */
        SUPPLIER_ISSUED_COMPANY_NOT_FOUND: 'errors.documents.supplier_issued_company_not_found',
        /** Il fornitore di un TD28 deve avere nazione SM (controllo SDI 00473). */
        SAN_MARINO_SUPPLIER_COUNTRY_INVALID: 'errors.documents.san_marino_supplier_country_invalid',
        /** Imponibile di riga non ammesso: serve un importo positivo. */
        SUPPLIER_ISSUED_LINE_AMOUNT_INVALID: 'errors.documents.supplier_issued_line_amount_invalid',
        /** Riga senza aliquota: su un TD28 l'IVA l'ha addebitata il fornitore. */
        SAN_MARINO_LINE_VAT_RATE_REQUIRED: 'errors.documents.san_marino_line_vat_rate_required',
        /** Riga TD29 senza né aliquota né natura, o con entrambe. */
        OMITTED_INVOICE_LINE_VAT_INVALID: 'errors.documents.omitted_invoice_line_vat_invalid',
        /** Numero e data della fattura irregolare vanno indicati insieme. */
        OMITTED_INVOICE_REFERENCE_INCOMPLETE: 'errors.documents.omitted_invoice_reference_incomplete',
        /** L'aliquota indicata non è nell'anagrafica aliquote del tenant. */
        SUPPLIER_ISSUED_VAT_RATE_UNRESOLVED: 'errors.documents.supplier_issued_vat_rate_unresolved',
        /** La natura indicata non è nell'anagrafica aliquote del tenant. */
        SUPPLIER_ISSUED_VAT_NATURE_UNRESOLVED: 'errors.documents.supplier_issued_vat_nature_unresolved',
        // Autoconsumo e cessioni gratuite senza rivalsa (TD27). ARRIVANO tutte
        // all'utente: nascono dal dialog "Registra autoconsumo o omaggio" della
        // lista fatture.
        /**
         * Riga senza aliquota: una cessione gratuita SENZA rivalsa ha comunque
         * l'IVA a debito. Aliquota zero significa Natura, e allora non è un TD27.
         */
        SELF_CONSUMPTION_LINE_VAT_RATE_REQUIRED: 'errors.documents.self_consumption_line_vat_rate_required',
        /** Quantità o costo di riga non ammessi: servono entrambi positivi. */
        SELF_CONSUMPTION_LINE_QUANTITY_INVALID: 'errors.documents.self_consumption_line_quantity_invalid',
        /** L'articolo indicato su una riga non esiste nel tenant. */
        SELF_CONSUMPTION_PRODUCT_NOT_FOUND: 'errors.documents.self_consumption_product_not_found',
        /**
         * Rete di sicurezza del costruttore del file: un documento che NON è
         * verso sé stessi è arrivato alla composizione senza controparte. Il
         * pre-check e la guard di invio lo fermano prima: qui non deve arrivare.
         */
        DOCUMENT_COUNTERPART_REQUIRED: 'errors.documents.document_counterpart_required',
        // Autofattura per splafonamento del plafond (TD21). ARRIVANO tutte
        // all'utente: nascono dal dialog "Regolarizza superamento del plafond"
        // della lista fatture.
        /**
         * Riga senza aliquota: il controllo SDI 00474 vieta al TD21 qualunque
         * riga ad aliquota zero. L'eccedenza di plafond è per definizione
         * imponibile: l'imposta che non è stata addebitata va dichiarata.
         */
        PLAFOND_OVERRUN_LINE_VAT_RATE_REQUIRED: 'errors.documents.plafond_overrun_line_vat_rate_required',
        /** Eccedenza di riga non ammessa: serve un imponibile positivo. */
        PLAFOND_OVERRUN_LINE_AMOUNT_INVALID: 'errors.documents.plafond_overrun_line_amount_invalid',
        /**
         * Nessuna fattura del fornitore citata: il TD21 regolarizza acquisti
         * precisi, e senza i loro estremi l'Agenzia non sa che cosa si sta
         * sanando.
         */
        PLAFOND_OVERRUN_INVOICE_REFERENCE_REQUIRED: 'errors.documents.plafond_overrun_invoice_reference_required',
        /**
         * Riferimento a metà: o si sceglie una fattura già in Previxio, o si
         * digitano numero E data. Mezzo riferimento non identifica nulla.
         */
        PLAFOND_OVERRUN_INVOICE_REFERENCE_INVALID: 'errors.documents.plafond_overrun_invoice_reference_invalid',
        /**
         * La fattura citata non è una passiva di quel fornitore: citarla
         * dichiarerebbe all'Agenzia un acquisto che non esiste.
         */
        PLAFOND_OVERRUN_INVOICE_NOT_FOUND: 'errors.documents.plafond_overrun_invoice_not_found',
        /**
         * La data della regolarizzazione non cade nell'anno dello splafonamento
         * dichiarato. La guida chiede una data dentro quell'anno.
         */
        PLAFOND_OVERRUN_YEAR_MISMATCH: 'errors.documents.plafond_overrun_year_mismatch',
        // Estrazione di beni da un deposito IVA (TD22 senza versamento, TD23 con
        // versamento dell'imposta). ARRIVANO tutte all'utente: nascono dal dialog
        // "Registra estrazione da deposito IVA" della lista fatture.
        /** La modalità di introduzione dei beni nel deposito non è riconosciuta. */
        VAT_WAREHOUSE_INTRODUCTION_INVALID: 'errors.documents.vat_warehouse_introduction_invalid',
        /**
         * Cedente italiano su un'estrazione di beni che l'azienda stessa aveva
         * introdotto nel deposito: quei beni sono arrivati da un altro Paese, e la
         * guida vuole il cedente estero con il suo paese di residenza.
         */
        VAT_WAREHOUSE_SUPPLIER_COUNTRY_INVALID: 'errors.documents.vat_warehouse_supplier_country_invalid',
        /**
         * Riferimento al documento che certifica l'acquisto precedente a metà: o
         * si sceglie una fattura già in Previxio, o si digitano numero E data
         * (è l'unica strada per la bolletta doganale, che qui non esiste come
         * documento). Mai entrambi, mai nessuno dei due.
         */
        VAT_WAREHOUSE_SOURCE_REFERENCE_INVALID: 'errors.documents.vat_warehouse_source_reference_invalid',
        /**
         * Il documento citato non è una fattura di acquisto di quel fornitore:
         * citarlo dichiarerebbe all'Agenzia un acquisto che non esiste.
         */
        VAT_WAREHOUSE_SOURCE_DOCUMENT_NOT_FOUND: 'errors.documents.vat_warehouse_source_document_not_found',
        /** Imponibile di riga non ammesso: serve un importo positivo. */
        VAT_WAREHOUSE_LINE_AMOUNT_INVALID: 'errors.documents.vat_warehouse_line_amount_invalid',
        /**
         * Riga senza aliquota su un'estrazione che l'imposta la deve: l'unico caso
         * senza imposta è il TD23 estratto con il plafond, e lì la riga porta la
         * Natura N3.5 al posto dell'aliquota (controlli SDI 00400/00401).
         */
        VAT_WAREHOUSE_LINE_VAT_RATE_REQUIRED: 'errors.documents.vat_warehouse_line_vat_rate_required',
        /**
         * Estrazione con il plafond chiesta su un TD22: il plafond vale solo per i
         * beni già presenti in Italia introdotti nel deposito (TD23).
         */
        VAT_WAREHOUSE_PLAFOND_NOT_ALLOWED: 'errors.documents.vat_warehouse_plafond_not_allowed',
        /**
         * Valore delle lavorazioni eseguite sui beni dentro il deposito non
         * ammesso: o è un importo positivo, o non si indica.
         */
        VAT_WAREHOUSE_SERVICES_AMOUNT_INVALID: 'errors.documents.vat_warehouse_services_amount_invalid',
        // Regolarizzazione TD20: la COPPIA autofattura + documento che porta
        // l'imposta. ARRIVANO tutte all'utente: nascono dal dialog "Regolarizza
        // fattura mancante o irregolare" della lista fatture.
        /** Il fornitore non ha il paese richiesto dalla casistica (IT o altro Stato UE). */
        REGULARIZATION_SUPPLIER_COUNTRY_INVALID: 'errors.documents.regularization_supplier_country_invalid',
        /** La natura di una riga non appartiene alla casistica scelta. */
        REGULARIZATION_VAT_NATURE_INVALID: 'errors.documents.regularization_vat_nature_invalid',
        /** Il codice TD scelto non può portare l'imposta per questa casistica. */
        REGULARIZATION_INTEGRATION_TYPE_INVALID: 'errors.documents.regularization_integration_type_invalid',
        /** Aliquota del documento integrativo non ammessa (deve essere > 0). */
        REGULARIZATION_VAT_RATE_INVALID: 'errors.documents.regularization_vat_rate_invalid',
        /** Il TD20 è nato senza numero: il documento integrativo non può citarlo. */
        REGULARIZATION_NUMBER_REQUIRED: 'errors.documents.regularization_number_required',
        // Rettifica di un documento a parti invertite o di un TD26 (guida AdE
        // v1.10). ARRIVANO tutte all'utente: nascono dall'azione "Rettifica" del
        // menu del documento originale.
        /** Il tipo del documento da rettificare non ammette una rettifica. */
        RECTIFICATION_TYPE_NOT_RECTIFIABLE: 'errors.documents.rectification_type_not_rectifiable',
        /** Il documento da rettificare non è ancora arrivato al SDI, o è scartato. */
        RECTIFICATION_SOURCE_NOT_TRANSMITTED: 'errors.documents.rectification_source_not_transmitted',
        /** Il documento da rettificare è senza numero: la rettifica non può citarlo. */
        RECTIFICATION_SOURCE_NUMBER_REQUIRED: 'errors.documents.rectification_source_number_required',
        /** Il documento da rettificare non ha righe da stornare. */
        RECTIFICATION_SOURCE_WITHOUT_LINES: 'errors.documents.rectification_source_without_lines',
        /** Numero e data della nota di variazione del fornitore vanno insieme. */
        RECTIFICATION_CREDIT_NOTE_REFERENCE_INCOMPLETE: 'errors.documents.rectification_credit_note_reference_incomplete',
    },
    // Document Lines module
    DOCUMENT_LINES: {
        NOT_FOUND: 'errors.document_lines.not_found',
        DOCUMENT_NOT_FOUND: 'errors.document_lines.document_not_found',
        INVALID_QUANTITY: 'errors.document_lines.invalid_quantity',
        INVALID_PRICE: 'errors.document_lines.invalid_price',
        COMPETENCE_PERIOD_INVALID: 'errors.document_lines.competence_period_invalid',
    },
    // SEPA Payments module
    SEPA_PAYMENTS: {
        INVALID_PROVIDER_RESPONSE: 'errors.sepa_payments.invalid_provider_response',
        NOT_FOUND: 'errors.sepa_payments.not_found',
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.sepa_payments.export_format_not_supported',
        CANNOT_DELETE_RECONCILED: 'errors.sepa_payments.cannot_delete_reconciled',
        CANNOT_DELETE_CONFIRMED: 'errors.sepa_payments.cannot_delete_confirmed',
        NOT_RETRIABLE: 'errors.sepa_payments.not_retriable',
        AMOUNT_EXCEEDS_TRANSFER_LIMIT: 'errors.sepa_payments.amount_exceeds_transfer_limit',
        INVALID_AMOUNT_RANGE: 'errors.sepa_payments.invalid_amount_range',
        // Azioni del backoffice sullo stato (servizio admin dei pagamenti SEPA).
        // Concorrenza ottimistica: lo stato letto dall'operatore non è più quello
        // a DB (un webhook o un altro operatore l'hanno cambiato nel frattempo).
        STATUS_CONFLICT: 'errors.sepa_payments.status_conflict',
        ORPHAN_REVIEW_NOT_ALLOWED: 'errors.sepa_payments.orphan_review_not_allowed',
        // Uscire da CONFIRMED con scadenze già riconciliate lascerebbe allocazioni
        // e link su un bonifico dichiarato non eseguito.
        CANNOT_LEAVE_CONFIRMED_RECONCILED: 'errors.sepa_payments.cannot_leave_confirmed_reconciled',
        // Reset di un PENDING che il provider potrebbe ancora eseguire: troppo
        // recente, oppure già eseguito lato remoto.
        CANNOT_RESET_RECENT_PENDING: 'errors.sepa_payments.cannot_reset_recent_pending',
        CANNOT_RESET_REMOTE_CONFIRMED: 'errors.sepa_payments.cannot_reset_remote_confirmed',
        // Segnare FAILED un bonifico che il provider dà per eseguito abiliterebbe
        // "Riprova" nella webapp: doppio bonifico.
        CANNOT_FAIL_REMOTE_CONFIRMED: 'errors.sepa_payments.cannot_fail_remote_confirmed',
        // Uscire da CONFIRMED quando il provider lo dà eseguito: per Fabrick,
        // senza specchio, si perderebbe l'unica prova dell'esecuzione.
        CANNOT_LEAVE_REMOTE_CONFIRMED: 'errors.sepa_payments.cannot_leave_remote_confirmed',
        // Il provider dà il bonifico ancora in corso (stato remoto noto non
        // terminale, es. `accepted`): segnarlo fallito o reimpostarlo lo
        // renderebbe ritentabile mentre può ancora essere eseguito.
        CANNOT_FAIL_REMOTE_IN_PROGRESS: 'errors.sepa_payments.cannot_fail_remote_in_progress',
        CANNOT_RESET_REMOTE_IN_PROGRESS: 'errors.sepa_payments.cannot_reset_remote_in_progress',
        // "Riattiva automatismo" su un bonifico non forzato a mano: il realign
        // scavalcherebbe la guardia anti-regressione del webhook.
        NOT_MANUALLY_SET: 'errors.sepa_payments.not_manually_set',
        // "Riattiva automatismo" su un CONFIRMED forzato a mano di un bonifico
        // senza specchio (Fabrick) che il provider non conferma (muto, 404, stato
        // non riconosciuto): azzerare il flag trasformerebbe il CONFIRMED manuale
        // nella prova del provider che il bonifico è eseguito.
        CANNOT_RESUME_UNVERIFIED_CONFIRMED: 'errors.sepa_payments.cannot_resume_unverified_confirmed',
        // "Riprova" dalla webapp su un FAILED che il provider dà eseguito o
        // ancora in corso: un nuovo bonifico sarebbe un doppio pagamento.
        RETRY_BLOCKED_REMOTE_ACTIVE: 'errors.sepa_payments.retry_blocked_remote_active',
        // Cambio stato verso lo stato già a DB: congelerebbe il webhook (flag
        // manuale) o riscriverebbe la data senza cambiare nulla.
        STATUS_UNCHANGED: 'errors.sepa_payments.status_unchanged',
        // `executedAt` ha senso solo per un bonifico confermato.
        EXECUTED_AT_REQUIRES_CONFIRMED: 'errors.sepa_payments.executed_at_requires_confirmed',
        // Il giorno di esecuzione non può essere nel futuro (calendario di Roma).
        EXECUTED_AT_IN_FUTURE: 'errors.sepa_payments.executed_at_in_future',
        // Sync puntuale chiesta per un provider che non la supporta.
        PROVIDER_SYNC_NOT_SUPPORTED: 'errors.sepa_payments.provider_sync_not_supported',
        // Azione admin su una riga dello specchio ACube senza bonifico locale
        // (incasso, o bonifico già reimpostato): non c'è nulla da cambiare.
        NO_LINKED_PAYMENT: 'errors.sepa_payments.no_linked_payment',
        // errorCode per il logging persistito: lettura dello stato Fabrick fallita
        // durante il polling. Non arriva all'utente.
        FABRICK_STATUS_POLL_FAILED: 'errors.sepa_payments.fabrick_status_poll_failed',
        // errorCode per il logging persistito: collegamento tardivo della riga
        // dello specchio ACube fallito DOPO il commit della disposizione (il
        // bonifico resta; lo ricollega il sync successivo). Non arriva all'utente.
        MIRROR_LATE_LINK_FAILED: 'errors.sepa_payments.mirror_late_link_failed',
        // errorCode per il logging persistito: un bonifico forzato a mano dal
        // backoffice contraddice l'esito terminale del provider (es. FAILED
        // manuale e remoto CONFIRMED). Non si sovrascrive: lo decide un operatore.
        MANUAL_STATUS_CONTRADICTS_REMOTE: 'errors.sepa_payments.manual_status_contradicts_remote',
        // errorCode per il logging persistito: il provider conferma un bonifico
        // in uscita che non ha più un `SepaPayment` locale (reimpostato o
        // sostituito da "Riprova"): possibile doppio pagamento.
        CONFIRMED_WITHOUT_LOCAL_PAYMENT: 'errors.sepa_payments.confirmed_without_local_payment',
        // errorCode per il logging persistito: bonifici PENDING locali che il
        // provider dà ancora in corso da troppo tempo (una riga per tenant e giro).
        REMOTE_IN_PROGRESS_STUCK: 'errors.sepa_payments.remote_in_progress_stuck',
        // errorCode per il logging persistito: disposizione in uscita senza
        // `SepaPayment` ancora aperta presso il provider oltre la soglia. UNA riga
        // aperta per orfano (`metadata.externalPaymentId`), non una per giro.
        ORPHAN_DISPOSITION_STUCK: 'errors.sepa_payments.orphan_disposition_stuck',
        // Disposizione creata presso il provider ma non salvata in locale (claim
        // del "Riprova" consumato, scadenza già collegata, conflitto o DB giù,
        // lock perso): la risorsa remota resta attiva, non c'è revoca via API.
        // È l'errorCode del log (con `reason`) e la chiave mostrata all'utente
        // quando la causa non ha già una chiave propria.
        DISPOSITION_ORPHANED: 'errors.sepa_payments.disposition_orphaned',
        // errorCode per il logging persistito: il provider ha risposto senza
        // l'UUID della disposizione. Non arriva all'utente (riceve
        // INVALID_PROVIDER_RESPONSE).
        DISPOSITION_MISSING_ID: 'errors.sepa_payments.disposition_missing_id',
    },
    // Document Payment Schedules module
    DOCUMENT_PAYMENT_SCHEDULES: {
        NOT_FOUND: 'errors.document_payment_schedules.not_found',
        DOCUMENT_NOT_FOUND: 'errors.document_payment_schedules.document_not_found',
        ALREADY_PAID: 'errors.document_payment_schedules.already_paid',
        AMOUNT_EXCEEDS_REMAINING: 'errors.document_payment_schedules.amount_exceeds_remaining',
        SHORTFALL_CHANGE_NOT_ALLOWED: 'errors.document_payment_schedules.shortfall_change_not_allowed',
        DUPLICATE_INSTALLMENT_NUMBERS: 'errors.document_payment_schedules.duplicate_installment_numbers',
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.document_payment_schedules.export_format_not_supported',
        INVALID_DATE_RANGE: 'errors.document_payment_schedules.invalid_date_range',
        INVALID_AMOUNT_RANGE: 'errors.document_payment_schedules.invalid_amount_range',
        PAYMENT_ALREADY_PENDING: 'errors.document_payment_schedules.payment_already_pending',
        PAYMENT_ALREADY_CONFIRMED: 'errors.document_payment_schedules.payment_already_confirmed',
        ALREADY_RECONCILED: 'errors.document_payment_schedules.already_reconciled',
        LOCKED_SCHEDULES_CONFLICT: 'errors.document_payment_schedules.locked_schedules_conflict',
    },
    // Accounts module
    ACCOUNTS: {
        FORECAST_HISTORY_INVALID: 'errors.accounts.forecast_history_invalid',
        FORECAST_HISTORY_CONFLICT: 'errors.accounts.forecast_history_conflict',
        BANK_SYNC_REVIEW_REQUIRED: 'errors.accounts.bank_sync_review_required',
        NOT_FOUND: 'errors.accounts.not_found',
        IBAN_ALREADY_EXISTS: 'errors.accounts.iban_already_exists',
        INVALID_IBAN: 'errors.accounts.invalid_iban',
        CANNOT_DELETE_WITH_MOVEMENTS: 'errors.accounts.cannot_delete_with_movements',
        ALREADY_DEFAULT: 'errors.accounts.already_default',
        SYNC_FAILED: 'errors.accounts.sync_failed',
        CANNOT_DELETE_DEFAULT_ACCOUNT: 'errors.accounts.cannot_delete_default_account',
        CANNOT_ARCHIVE_DEFAULT_ACCOUNT: 'errors.accounts.cannot_archive_default_account',
        ARCHIVED: 'errors.accounts.archived',
        RECORD_IN_USE: 'errors.accounts.record_in_use',
        BALANCE_RECALCULATION_FAILED: 'errors.accounts.balance_recalculation_failed',
        NOT_ENABLED: 'errors.accounts.not_enabled',
        REJECTED: 'errors.accounts.rejected',
        DUPLICATED: 'errors.accounts.duplicated',
        CANNOT_SYNC_MANUAL_ACCOUNT: 'errors.accounts.cannot_sync_manual_account',
        CURRENCY_NOT_SUPPORTED_FOR_IMPORT: 'errors.accounts.currency_not_supported_for_import',
        PROVIDER_CURRENCY_MISSING: 'errors.accounts.provider_currency_missing',
        LINKED_BANK_NOT_FOUND: 'errors.accounts.linked_bank_not_found',
        CARD_REQUIRES_BANK_LINK: 'errors.accounts.card_requires_bank_link',
        LINKED_PORTFOLIO_REQUIRES_SBF_SOURCE: 'errors.accounts.linked_portfolio_requires_sbf_source',
        LINKED_PORTFOLIO_TARGET_MUST_BE_MAIN_BANK: 'errors.accounts.linked_portfolio_target_must_be_main_bank',
        OPEN_BANKING_NOT_CONNECTED: 'errors.accounts.open_banking_not_connected',
        MANUAL_OVERRIDE_CONFIRMATION_REQUIRED: 'errors.accounts.manual_override_confirmation_required',
        TYPE_CHANGE_REQUIRES_BALANCE_ACCESS: 'errors.accounts.type_change_requires_balance_access',
        FABRICK_CREDITOR_ACCOUNT_NOT_CONFIGURED: 'errors.accounts.fabrick_creditor_account_not_configured',
        FABRICK_DEBTOR_ACCOUNT_NOT_CONFIGURED: 'errors.accounts.fabrick_debtor_account_not_configured',
        FABRICK_INSTANT_NOT_AVAILABLE: 'errors.accounts.fabrick_instant_not_available',
        FABRICK_RECEIVE_NOT_AVAILABLE: 'errors.accounts.fabrick_receive_not_available',
        // errorCode per il logging persistito: uno step per-conto del sync
        // (saldo, movimenti, storico) è fallito senza far cadere gli altri conti.
        SYNC_STEP_FAILED: 'errors.accounts.sync_step_failed',
        // Idem, ma la causa è una finestra DB transitoria (compute serverless in
        // sola lettura): il retry la recupera, quindi è un warning e non un
        // guasto. Il conteggio serve a misurare quanto pesano quelle finestre.
        SYNC_STEP_TRANSIENT_DB_ERROR: 'errors.accounts.sync_step_transient_db_error',
        // errorCode per il logging persistito: archiviato o cancellato il conto,
        // il provider non ha disattivato il collegamento remoto (l'operazione
        // locale resta valida; `operation` nei metadata dice quale).
        PROVIDER_ACCOUNT_DISABLE_FAILED: 'errors.accounts.provider_account_disable_failed',
    },
    // Cash Movements module
    CASH_MOVEMENTS: {
        PRIMA_NOTA_CURRENCY_DATA_MISSING: 'errors.cash_movements.prima_nota_currency_data_missing',
        NOT_FOUND: 'errors.cash_movements.not_found',
        ACCOUNT_NOT_FOUND: 'errors.cash_movements.account_not_found',
        CATEGORY_NOT_FOUND: 'errors.cash_movements.category_not_found',
        INVALID_AMOUNT: 'errors.cash_movements.invalid_amount',
        AMOUNT_BELOW_EXPLAINED: 'errors.cash_movements.amount_below_explained',
        INVALID_DATE: 'errors.cash_movements.invalid_date',
        ALREADY_RECONCILED: 'errors.cash_movements.already_reconciled',
        ACCOUNT_IS_AUTOSYNC: 'errors.cash_movements.account_is_autosync',
        ACCOUNT_ARCHIVED: 'errors.cash_movements.account_archived',
        SYNCED_FROM_BANK: 'errors.cash_movements.synced_from_bank',
        DELETE_MANY_FAILED: 'errors.cash_movements.delete_many_failed',
        CATEGORY_LOCKED_BY_DOCUMENT: 'errors.cash_movements.category_locked_by_document',
        CATEGORY_LOCKED_MULTIPLE_DOCUMENTS: 'errors.cash_movements.category_locked_multiple_documents',
        CATEGORY_LOCKED_BY_INTERNAL_TRANSFER: 'errors.cash_movements.category_locked_by_internal_transfer',
        CANNOT_CREATE_RECURRENCE_FROM_TRANSFER: 'errors.cash_movements.cannot_create_recurrence_from_transfer',
        ALREADY_LINKED_TO_RECURRENCE: 'errors.cash_movements.already_linked_to_recurrence',
        CANNOT_RECONCILE_EXCLUDED: 'errors.cash_movements.cannot_reconcile_excluded',
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.cash_movements.export_format_not_supported',
        INVALID_AMOUNT_RANGE: 'errors.cash_movements.invalid_amount_range',
    },
    // Cash Movement Allocations module
    CASH_MOVEMENT_ALLOCATION: {
        CONFIRMED_ALLOCATION_IMMUTABLE: 'errors.cash_movement_allocation.confirmed_allocation_immutable',
        NOT_FOUND: 'errors.cash_movement_allocation.not_found',
        CASH_MOVEMENT_NOT_FOUND: 'errors.cash_movement_allocation.cash_movement_not_found',
        SCHEDULE_NOT_FOUND: 'errors.cash_movement_allocation.schedule_not_found',
        DIRECTION_MISMATCH: 'errors.cash_movement_allocation.direction_mismatch',
        AMOUNT_EXCEEDS_REMAINING: 'errors.cash_movement_allocation.amount_exceeds_remaining',
        INVALID_AMOUNT: 'errors.cash_movement_allocation.invalid_amount',
        DUPLICATE_SCHEDULE: 'errors.cash_movement_allocation.duplicate_schedule',
        NETTING_NET_MUST_BE_POSITIVE: 'errors.cash_movement_allocation.netting_net_must_be_positive',
    },
    // Cash Movement Splits (righe libere) module
    CASH_MOVEMENT_SPLIT: {
        NOT_FOUND: 'errors.cash_movement_split.not_found',
        CASH_MOVEMENT_NOT_FOUND: 'errors.cash_movement_split.movement_not_found',
        MOVEMENT_NOT_ELIGIBLE: 'errors.cash_movement_split.movement_not_eligible',
        INVALID_AMOUNT: 'errors.cash_movement_split.invalid_amount',
        AMOUNT_EXCEEDS_REMAINING: 'errors.cash_movement_split.amount_exceeds_remaining',
        CATEGORY_NOT_FOUND: 'errors.cash_movement_split.category_not_found',
        COMPANY_NOT_FOUND: 'errors.cash_movement_split.company_not_found',
        CATEGORY_DIRECTION_MISMATCH: 'errors.cash_movement_split.category_direction_mismatch',
        DUPLICATE_ROW: 'errors.cash_movement_split.duplicate_row',
    },
    // Payments module
    PAYMENTS: {
        NOT_FOUND: 'errors.payments.not_found',
        DOCUMENT_NOT_FOUND: 'errors.payments.document_not_found',
        SCHEDULE_NOT_FOUND: 'errors.payments.schedule_not_found',
        CASH_MOVEMENT_NOT_FOUND: 'errors.payments.cash_movement_not_found',
        AMOUNT_EXCEEDS_REMAINING: 'errors.payments.amount_exceeds_remaining',
        INVALID_AMOUNT: 'errors.payments.invalid_amount',
        AT_LEAST_ONE_PAYMENT_REQUIRED: 'errors.payments.at_least_one_payment_required',
        AT_LEAST_ONE_ALLOCATION_REQUIRED: 'errors.payments.at_least_one_allocation_required',
    },
    // Categories module
    CATEGORIES: {
        NOT_FOUND: 'errors.categories.not_found',
        NAME_ALREADY_EXISTS: 'errors.categories.name_already_exists',
        CANNOT_DELETE_WITH_TRANSACTIONS: 'errors.categories.cannot_delete_with_transactions',
        PARENT_NOT_FOUND: 'errors.categories.parent_not_found',
        MAX_DEPTH_EXCEEDED: 'errors.categories.max_depth_exceeded',
        CIRCULAR_REFERENCE: 'errors.categories.circular_reference',
        CANNOT_CONVERT_WITH_CHILDREN: 'errors.categories.cannot_convert_with_children',
        CANNOT_DELETE_WITH_CHILDREN: 'errors.categories.cannot_delete_with_children',
        FLOW_TYPE_MISMATCH: 'errors.categories.flow_type_mismatch',
        SUBCATEGORY_FLOW_TYPE_MISMATCH: 'errors.categories.subcategory_flow_type_mismatch',
        NOT_ASSIGNABLE: 'errors.categories.not_assignable',
        NOT_EDITABLE: 'errors.categories.not_editable',
        NOT_DELETABLE: 'errors.categories.not_deletable',
        SYSTEM_LOCKED: 'errors.categories.system_locked',
        CANNOT_DELETE_WITH_DOCUMENTS: 'errors.categories.cannot_delete_with_documents',
        CANNOT_DELETE_WITH_REFERENCES: 'errors.categories.cannot_delete_with_references',
        SYSTEM_NOT_ARCHIVABLE: 'errors.categories.system_not_archivable',
        ARCHIVED: 'errors.categories.archived',
        PARENT_ARCHIVED: 'errors.categories.parent_archived',
    },
    // Parole chiave delle categorie (suggerimento automatico)
    CATEGORY_KEYWORDS: {
        NOT_FOUND: 'errors.category_keywords.not_found',
    },
    // Commesse module (controllo di gestione)
    COMMESSE: {
        CODE_ALREADY_EXISTS: 'errors.commesse.code_already_exists',
        NOT_FOUND: 'errors.commesse.not_found',
        NAME_ALREADY_EXISTS: 'errors.commesse.name_already_exists',
        COMPANY_NOT_FOUND: 'errors.commesse.company_not_found',
        ALLOCATION_SUM_EXCEEDED: 'errors.commesse.allocation_sum_exceeded',
        ALLOCATION_DUPLICATE: 'errors.commesse.allocation_duplicate',
        FEATURE_DISABLED: 'errors.commesse.feature_disabled',
        CONTRACT_ACCESS_REQUIRED: 'errors.commesse.contract_access_required',
    },
    HOTEL: {
        FEATURE_DISABLED: 'errors.hotel.feature_disabled',
        FORECAST_PROTECTED: 'errors.hotel.forecast_protected',
    },
    // Service contracts (canoni annuali) module
    SERVICE_CONTRACTS: {
        NOT_FOUND: 'errors.service_contracts.not_found',
        CODE_ALREADY_EXISTS: 'errors.service_contracts.code_already_exists',
        CUSTOMER_NOT_FOUND: 'errors.service_contracts.customer_not_found',
        PRODUCT_NOT_FOUND: 'errors.service_contracts.product_not_found',
        VAT_RATE_NOT_FOUND: 'errors.service_contracts.vat_rate_not_found',
        COMMESSA_NOT_FOUND: 'errors.service_contracts.commessa_not_found',
        COMMESSA_CUSTOMER_MISMATCH: 'errors.service_contracts.commessa_customer_mismatch',
        LINE_NOT_FOUND: 'errors.service_contracts.line_not_found',
        CHANGE_NOT_FOUND: 'errors.service_contracts.change_not_found',
        INVALID_STATUS: 'errors.service_contracts.invalid_status',
        VERSION_CONFLICT: 'errors.service_contracts.version_conflict',
        LINES_REQUIRED: 'errors.service_contracts.lines_required',
        LINE_TARGET_REQUIRED: 'errors.service_contracts.line_target_required',
        INVALID_DATE: 'errors.service_contracts.invalid_date',
        DATE_OUTSIDE_PERIOD: 'errors.service_contracts.date_outside_period',
        PERIOD_CLOSED: 'errors.service_contracts.period_closed',
        INVALID_QUANTITY: 'errors.service_contracts.invalid_quantity',
        INVALID_PRICE: 'errors.service_contracts.invalid_price',
        AMOUNT_OUT_OF_RANGE: 'errors.service_contracts.amount_out_of_range',
        QUANTITY_BELOW_ZERO: 'errors.service_contracts.quantity_below_zero',
        REDUCTION_NOT_AT_RENEWAL: 'errors.service_contracts.reduction_not_at_renewal',
        NEW_LINE_REDUCTION: 'errors.service_contracts.new_line_reduction',
        CHANGE_ALREADY_EFFECTIVE: 'errors.service_contracts.change_already_effective',
        REQUEST_KEY_CONFLICT: 'errors.service_contracts.request_key_conflict',
        CHANGE_CHARGES_INVOICED: 'errors.service_contracts.change_charges_invoiced',
        RENEWAL_NOT_DUE: 'errors.service_contracts.renewal_not_due',
        RENEWAL_LINE_UNKNOWN: 'errors.service_contracts.renewal_line_unknown',
        FEATURE_DISABLED: 'errors.service_contracts.feature_disabled',
        PERIOD_NOT_FOUND: 'errors.service_contracts.period_not_found',
        BILLING_REVISION_REQUIRED: 'errors.service_contracts.billing_revision_required',
        BILLING_INVOICE_STOPPED: 'errors.service_contracts.billing_invoice_stopped',
        CHARGES_REQUIRED: 'errors.service_contracts.charges_required',
        CHARGE_NOT_BILLABLE: 'errors.service_contracts.charge_not_billable',
        INVOICED_LINE_LOCKED: 'errors.service_contracts.invoiced_line_locked',
        DOCUMENT_RESTORE_CHARGES_CONFLICT: 'errors.service_contracts.document_restore_charges_conflict',
        CHARGES_CHANGED_CONCURRENTLY: 'errors.service_contracts.charges_changed_concurrently',
        CREDIT_NOTE_CHARGES_VOIDED: 'errors.service_contracts.credit_note_charges_voided',
        RECURRING_TEMPLATE_NOT_FOUND: 'errors.service_contracts.recurring_template_not_found',
        RECURRING_TEMPLATE_NOT_CONVERTIBLE: 'errors.service_contracts.recurring_template_not_convertible',
        /** Solo log: rinnovo automatico fallito per un contratto. */
        AUTO_RENEWAL_FAILED: 'errors.service_contracts.auto_renewal_failed',
        INVALID_DATE_RANGE: 'errors.service_contracts.invalid_date_range',
        INVALID_AMOUNT_RANGE: 'errors.service_contracts.invalid_amount_range',
    },
    MACHINES: {
        FEATURE_DISABLED: 'errors.machines.feature_disabled',
        ADDRESS_LOOKUP_UNAVAILABLE: 'errors.machines.address_lookup_unavailable',
        ADDRESS_LOOKUP_FAILED: 'errors.machines.address_lookup_failed',
        NOT_FOUND: 'errors.machines.not_found',
        CODE_EXISTS: 'errors.machines.code_exists',
        FIELD_REQUIRED: 'errors.machines.field_required',
        VERSION_CONFLICT: 'errors.machines.version_conflict',
        CUSTOMER_NOT_FOUND: 'errors.machines.customer_not_found',
        PRODUCT_NOT_FOUND: 'errors.machines.product_not_found',
        ARCHIVED: 'errors.machines.archived',
        NOT_DELETABLE: 'errors.machines.not_deletable',
        NO_CURRENT_ASSIGNMENT: 'errors.machines.no_current_assignment',
        TRANSFER_SAME_CUSTOMER: 'errors.machines.transfer_same_customer',
        TRANSFER_DATE_INVALID: 'errors.machines.transfer_date_invalid',
        TRANSFER_DATE_IN_FUTURE: 'errors.machines.transfer_date_in_future',
        TRANSFER_BEFORE_HISTORY: 'errors.machines.transfer_before_history',
        TRANSFER_BLOCKED_BY_DRAFTS: 'errors.machines.transfer_blocked_by_drafts',
        COVERAGE_NOT_FOUND: 'errors.machines.coverage_not_found',
        COVERAGE_CONTRACT_NOT_FOUND: 'errors.machines.coverage_contract_not_found',
        COVERAGE_CONTRACT_OTHER_CUSTOMER: 'errors.machines.coverage_contract_other_customer',
        COVERAGE_OVERLAP: 'errors.machines.coverage_overlap',
        INVALID_INTERVAL: 'errors.machines.invalid_interval',
        ATTACHMENT_NOT_FOUND: 'errors.machines.attachment_not_found',
        ATTACHMENT_STORAGE_CLEANUP_FAILED: 'errors.machines.attachment_storage_cleanup_failed',
        IMPORT_INVALID_FILE: 'errors.machines.import_invalid_file',
        IMPORT_HAS_ERRORS: 'errors.machines.import_has_errors',
    },
    MACHINE_CHECKS: {
        CHECKLIST_NOT_FOUND: 'errors.machine_checks.checklist_not_found',
        CHECKLIST_CODE_EXISTS: 'errors.machine_checks.checklist_code_exists',
        CHECKLIST_INVALID_ITEMS: 'errors.machine_checks.checklist_invalid_items',
        CHECKLIST_IN_USE: 'errors.machine_checks.checklist_in_use',
        CHECKLIST_INACTIVE: 'errors.machine_checks.checklist_inactive',
        PLAN_CHECKLIST_MISMATCH: 'errors.machine_checks.plan_checklist_mismatch',
        PLAN_CYCLE_CHANGED: 'errors.machine_checks.plan_cycle_changed',
        PLAN_NOT_FOUND: 'errors.machine_checks.plan_not_found',
        PLAN_INVALID_RECURRENCE: 'errors.machine_checks.plan_invalid_recurrence',
        PLAN_NOT_ACTIVE: 'errors.machine_checks.plan_not_active',
        PLAN_VERSION_CONFLICT: 'errors.machine_checks.plan_version_conflict',
        NOT_FOUND: 'errors.machine_checks.not_found',
        VERSION_CONFLICT: 'errors.machine_checks.version_conflict',
        INVALID_STATUS: 'errors.machine_checks.invalid_status',
        INCOMPLETE: 'errors.machine_checks.incomplete',
        OUTCOME_REQUIRED: 'errors.machine_checks.outcome_required',
        OUTCOME_INCONSISTENT: 'errors.machine_checks.outcome_inconsistent',
        NOT_PERFORMED_REASON_REQUIRED: 'errors.machine_checks.not_performed_reason_required',
        EXECUTED_IN_FUTURE: 'errors.machine_checks.executed_in_future',
        EXECUTED_OUTSIDE_ASSIGNMENT: 'errors.machine_checks.executed_outside_assignment',
        EXECUTED_ON_REQUIRED: 'errors.machine_checks.executed_on_required',
        DUE_ALREADY_SATISFIED: 'errors.machine_checks.due_already_satisfied',
        REOPEN_BLOCKED: 'errors.machine_checks.reopen_blocked',
        WORK_REPORT_INVALID: 'errors.machine_checks.work_report_invalid',
        MACHINE_UNAVAILABLE: 'errors.machine_checks.machine_unavailable',
        CUSTOMER_SIGNATURE_REQUIRED: 'errors.machine_checks.customer_signature_required',
        FINDING_NOT_FOUND: 'errors.machine_checks.finding_not_found',
        FINDING_ALREADY_RESOLVED: 'errors.machine_checks.finding_already_resolved',
        TECHNICIAN_NOT_FOUND: 'errors.machine_checks.technician_not_found',
        RESPONSIBLE_NOT_FOUND: 'errors.machine_checks.responsible_not_found',
        /** Solo log: avviso di scadenza non creato. */
        REMINDER_FAILED: 'errors.machine_checks.reminder_failed',
        /** Solo log: giro degli avvisi fallito. */
        REMINDERS_RUN_FAILED: 'errors.machine_checks.reminders_run_failed',
    },
    WORK_REPORTS: {
        TRAVEL_ADDRESS_FAILED: 'errors.work_reports.travel_address_failed',
        MACHINE_NOT_ASSIGNED: 'errors.work_reports.machine_not_assigned',
        MACHINE_NOT_IN_REPORT: 'errors.work_reports.machine_not_in_report',
        MACHINE_HAS_LINKED_CHECKS: 'errors.work_reports.machine_has_linked_checks',
        MACHINE_CHECKS_NOT_APPROVED: 'errors.work_reports.machine_checks_not_approved',
        CUSTOMER_SIGNATURE_REQUIRED: 'errors.work_reports.customer_signature_required',
        TRAVEL_UNAVAILABLE: 'errors.work_reports.travel_unavailable',
        TRAVEL_ROUTE_FAILED: 'errors.work_reports.travel_route_failed',
        TRAVEL_INVALID_SETTINGS: 'errors.work_reports.travel_invalid_settings',
        TRAVEL_CUSTOMER_NOT_FOUND: 'errors.work_reports.travel_customer_not_found',
        TRAVEL_VEHICLE_NOT_FOUND: 'errors.work_reports.travel_vehicle_not_found',
        TRAVEL_SETTINGS_FORBIDDEN: 'errors.work_reports.travel_settings_forbidden',
        NOT_FOUND: 'errors.work_reports.not_found',
        TARIFF_NOT_FOUND: 'errors.work_reports.tariff_not_found',
        TARIFF_CODE_EXISTS: 'errors.work_reports.tariff_code_exists',
        TARIFF_INACTIVE: 'errors.work_reports.tariff_inactive',
        TARIFF_IN_USE: 'errors.work_reports.tariff_in_use',
        TECHNICIAN_NOT_FOUND: 'errors.work_reports.technician_not_found',
        CUSTOMER_NOT_FOUND: 'errors.work_reports.customer_not_found',
        CUSTOMER_NOT_SELECTABLE: 'errors.work_reports.customer_not_selectable',
        CONTRACT_NOT_FOUND: 'errors.work_reports.contract_not_found',
        CONTRACT_CUSTOMER_MISMATCH: 'errors.work_reports.contract_customer_mismatch',
        CONTRACT_REQUIRED: 'errors.work_reports.contract_required',
        CONTRACT_NOT_ACTIVE: 'errors.work_reports.contract_not_active',
        COMMESSA_NOT_FOUND: 'errors.work_reports.commessa_not_found',
        COMMESSA_CUSTOMER_MISMATCH: 'errors.work_reports.commessa_customer_mismatch',
        PRODUCT_NOT_FOUND: 'errors.work_reports.product_not_found',
        VAT_RATE_NOT_FOUND: 'errors.work_reports.vat_rate_not_found',
        STOCK_DOCUMENT_NOT_FOUND: 'errors.work_reports.stock_document_not_found',
        CONTRACT_LINE_NOT_FOUND: 'errors.work_reports.contract_line_not_found',
        LICENSE_DATE_REQUIRED: 'errors.work_reports.license_date_required',
        INVALID_STATUS: 'errors.work_reports.invalid_status',
        VERSION_CONFLICT: 'errors.work_reports.version_conflict',
        INVALID_TIME: 'errors.work_reports.invalid_time',
        SESSION_TIMES_REQUIRED: 'errors.work_reports.session_times_required',
        END_BEFORE_START: 'errors.work_reports.end_before_start',
        ZERO_DURATION: 'errors.work_reports.zero_duration',
        BREAK_TOO_LONG: 'errors.work_reports.break_too_long',
        DURATION_TOO_LONG: 'errors.work_reports.duration_too_long',
        ACTIVITIES_REQUIRED: 'errors.work_reports.activities_required',
        ACTIVITIES_MISMATCH: 'errors.work_reports.activities_mismatch',
        INVALID_MINUTES: 'errors.work_reports.invalid_minutes',
        INVALID_QUANTITY: 'errors.work_reports.invalid_quantity',
        INVALID_PRICE: 'errors.work_reports.invalid_price',
        PRICE_REQUIRED: 'errors.work_reports.price_required',
        UNPRICED_LINES: 'errors.work_reports.unpriced_lines',
        CONTENT_REQUIRED: 'errors.work_reports.content_required',
        SELF_APPROVAL_FORBIDDEN: 'errors.work_reports.self_approval_forbidden',
        RECTIFY_INVOICED: 'errors.work_reports.rectify_invoiced',
        RECTIFY_LICENSE_EFFECTIVE: 'errors.work_reports.rectify_license_effective',
        FEATURE_DISABLED: 'errors.work_reports.feature_disabled',
        RECTIFY_DRAFT_INVOICE: 'errors.work_reports.rectify_draft_invoice',
        RECTIFY_CONCURRENT: 'errors.work_reports.rectify_concurrent',
        CONTRACT_ACCESS_REQUIRED: 'errors.work_reports.contract_access_required',
        COMMESSA_ACCESS_REQUIRED: 'errors.work_reports.commessa_access_required',
        EDIT_FORBIDDEN: 'errors.work_reports.edit_forbidden',
        CODE_EXHAUSTED: 'errors.work_reports.code_exhausted',
        CONTRACT_IN_USE: 'errors.work_reports.contract_in_use',
        TECHNICIAN_IN_USE: 'errors.work_reports.technician_in_use',
        INVALID_DATE_RANGE: 'errors.work_reports.invalid_date_range',
        SECTION_LINK_STALE: 'errors.work_reports.section_link_stale',
        CUSTOMER_LOCKED_BY_CONTRACT: 'errors.work_reports.customer_locked_by_contract',
        CUSTOMER_LOCKED_BY_MACHINES: 'errors.work_reports.customer_locked_by_machines',
        CUSTOMER_LOCKED_BY_COMMESSA: 'errors.work_reports.customer_locked_by_commessa',
        TARIFFS_CHANGED: 'errors.work_reports.tariffs_changed',
        APPROVAL_CHARGES_MISMATCH: 'errors.work_reports.approval_charges_mismatch',
        DELETE_RECTIFIED: 'errors.work_reports.delete_rectified',
        STOCK_DOCUMENT_DRAFT: 'errors.work_reports.stock_document_draft',
        STOCK_DOCUMENT_UNLINK_FORBIDDEN: 'errors.work_reports.stock_document_unlink_forbidden',
        COST_RATE_NOT_FOUND: 'errors.work_reports.cost_rate_not_found',
        COST_RATE_EXISTS: 'errors.work_reports.cost_rate_exists',
        COST_RATE_IN_USE: 'errors.work_reports.cost_rate_in_use',
        PUBLICATION_NOT_FOUND: 'errors.work_reports.publication_not_found',
        NOT_PUBLISHED: 'errors.work_reports.not_published',
        SEND_DUPLICATE: 'errors.work_reports.send_duplicate',
        PUBLICATION_SECTION_REQUIRED: 'errors.work_reports.publication_section_required',
        SNAPSHOT_INTEGRITY: 'errors.work_reports.snapshot_integrity',
        SNAPSHOT_STORAGE_UNAVAILABLE: 'errors.work_reports.snapshot_storage_unavailable',
        BULK_APPROVAL_FAILED: 'errors.work_reports.bulk_approval_failed',
        SHARE_LINK_NOT_FOUND: 'errors.work_reports.share_link_not_found',
        CUSTOMER_VAT_REQUIRED: 'errors.work_reports.customer_vat_required',
        CUSTOMER_VAT_INVALID: 'errors.work_reports.customer_vat_invalid',
        CUSTOMER_TAX_CODE_REQUIRED: 'errors.work_reports.customer_tax_code_required',
        CUSTOMER_TAX_CODE_INVALID: 'errors.work_reports.customer_tax_code_invalid',
        CUSTOMER_IDENTITY_UNAVAILABLE: 'errors.work_reports.customer_identity_unavailable',
        AUTO_PUBLISH_FAILED: 'errors.work_reports.auto_publish_failed',
    },
    // Compensazioni fra note di credito e fatture
    DOCUMENT_COMPENSATIONS: {
        LOCKED: 'errors.document_compensations.locked',
    },
    // Forecast Series module
    FORECAST_SERIES: {
        NOT_FOUND: 'errors.forecast_series.not_found',
        NAME_ALREADY_EXISTS: 'errors.forecast_series.name_already_exists',
        NAME_ARCHIVED_EXISTS: 'errors.forecast_series.name_archived_exists',
        INVALID_FREQUENCY: 'errors.forecast_series.invalid_frequency',
        INVALID_DATE_RANGE: 'errors.forecast_series.invalid_date_range',
        PERCENTAGES_MUST_SUM_100: 'errors.forecast_series.percentages_must_sum_100',
        AT_LEAST_ONE_TEMPLATE_REQUIRED: 'errors.forecast_series.at_least_one_template_required',
        TEMPLATES_COUNT_MISMATCH: 'errors.forecast_series.templates_count_mismatch',
        START_END_MONTH_CONFLICT: 'errors.forecast_series.start_end_month_conflict',
        GENERATING_OCCURRENCES: 'errors.forecast_series.generating_occurrences',
        DELETE_FAILED: 'errors.forecast_series.delete_failed',
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.forecast_series.export_format_not_supported',
        INVALID_AMOUNT_RANGE: 'errors.forecast_series.invalid_amount_range',
    },
    // Forecast Occurrences module
    FORECAST_OCCURRENCES: {
        NOT_FOUND: 'errors.forecast_occurrences.not_found',
        SERIES_NOT_FOUND: 'errors.forecast_occurrences.series_not_found',
        INVALID_STATUS: 'errors.forecast_occurrences.invalid_status',
        ALREADY_REALIZED: 'errors.forecast_occurrences.already_realized',
        PROTECTED_OCCURRENCE: 'errors.forecast_occurrences.protected_occurrence',
        DUPLICATE: 'errors.forecast_occurrences.duplicate',
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.forecast_occurrences.export_format_not_supported',
        INVALID_AMOUNT_RANGE: 'errors.forecast_occurrences.invalid_amount_range',
    },
    // Forecast Payment Schedules module
    FORECAST_PAYMENT_SCHEDULES: {
        NOT_FOUND: 'errors.forecast_payment_schedules.not_found',
        OCCURRENCE_NOT_FOUND: 'errors.forecast_payment_schedules.occurrence_not_found',
        ALREADY_LINKED: 'errors.forecast_payment_schedules.already_linked',
        NOT_LINKED: 'errors.forecast_payment_schedules.not_linked',
        DOCUMENT_LINKED: 'errors.forecast_payment_schedules.document_linked',
        HAS_CASH_RECEIPTS: 'errors.forecast_payment_schedules.has_cash_receipts',
        AMOUNT_BELOW_RECEIVED: 'errors.forecast_payment_schedules.amount_below_received',
        PLAN_TOTAL_MISMATCH: 'errors.forecast_payment_schedules.plan_total_mismatch',
        EMPTY_PLAN: 'errors.forecast_payment_schedules.empty_plan',
        DUPLICATE_PLAN_ITEM: 'errors.forecast_payment_schedules.duplicate_plan_item',
    },
    // Forecast Scenario module
    FORECAST_SCENARIO: {
        NOT_FOUND: 'errors.forecast_scenario.not_found',
        NAME_ALREADY_EXISTS: 'errors.forecast_scenario.name_already_exists',
        LOCKED: 'errors.forecast_scenario.locked',
        BASELINE_EXISTS: 'errors.forecast_scenario.baseline_exists',
        BASELINE_LOCKED: 'errors.forecast_scenario.baseline_locked',
        BASELINE_NOT_LOCKABLE: 'errors.forecast_scenario.baseline_not_lockable',
        CANNOT_DELETE_BASELINE: 'errors.forecast_scenario.cannot_delete_baseline',
    },
    // VAT Rates module
    VAT_RATES: {
        NOT_FOUND: 'errors.vat_rates.not_found',
        CODE_ALREADY_EXISTS: 'errors.vat_rates.code_already_exists',
        INVALID_RATE: 'errors.vat_rates.invalid_rate',
        CANNOT_DELETE_SYSTEM_DEFINED: 'errors.vat_rates.cannot_delete_system_defined',
    },
    // Municipalities module
    MUNICIPALITIES: {
        NOT_FOUND: 'errors.municipalities.not_found',
        SELECTION_REQUIRED: 'errors.municipalities.selection_required',
        SELECTION_INVALID: 'errors.municipalities.selection_invalid',
        SELECTION_MISMATCH: 'errors.municipalities.selection_mismatch',
        // Mostrato all'utente: il Paese deve essere il codice ISO 3166-1 alpha-2
        // (`[A-Z]{2}`), l'unica forma che il tracciato FatturaPA ammette in
        // `IdPaese`/`Nazione`.
        COUNTRY_CODE_INVALID: 'errors.municipalities.country_code_invalid',
    },
    // Tenant VAT Rates module
    TENANT_VAT_RATES: {
        NOT_FOUND: 'errors.tenant_vat_rates.not_found',
        VAT_RATE_NOT_FOUND: 'errors.tenant_vat_rates.vat_rate_not_found',
        ALREADY_EXISTS: 'errors.tenant_vat_rates.already_exists',
        CANNOT_DELETE_DEFAULT: 'errors.tenant_vat_rates.cannot_delete_default',
    },
    // Tenant Units of Measure module
    TENANT_UNITS_OF_MEASURE: {
        NOT_FOUND: 'errors.tenant_units_of_measure.not_found',
        ALREADY_EXISTS: 'errors.tenant_units_of_measure.already_exists',
    },
    // Tenant Payment Methods module
    TENANT_PAYMENT_METHODS: {
        NOT_FOUND: 'errors.tenant_payment_methods.not_found',
        ALREADY_EXISTS: 'errors.tenant_payment_methods.already_exists',
    },
    // Products module (anagrafica articoli)
    PRODUCTS: {
        NOT_FOUND: 'errors.products.not_found',
        CODE_ALREADY_EXISTS: 'errors.products.code_already_exists',
        RECORD_IN_USE: 'errors.products.record_in_use',
        INVENTORY_REQUIRES_STOCKED_TYPE: 'errors.products.inventory_requires_stocked_type',
    },
    // Price List module (listini prezzo: scaglioni + regole)
    PRICE_LIST: {
        NOT_FOUND: 'errors.price_list.not_found',
        DUPLICATE_CODE: 'errors.price_list.duplicate_code',
        INVALID_VALIDITY: 'errors.price_list.invalid_validity',
        ITEM_DUPLICATE: 'errors.price_list.item_duplicate',
        TIER_OVERLAP: 'errors.price_list.tier_overlap',
        PRODUCT_NOT_FOUND: 'errors.price_list.product_not_found',
    },
    // Stock module (rettifiche, trasferimenti, giacenze)
    STOCK: {
        ADJUSTMENT_NOTE_REQUIRED: 'errors.stock.adjustment_note_required',
        ADJUSTMENT_DELTA_ZERO: 'errors.stock.adjustment_delta_zero',
        NEGATIVE_BALANCE: 'errors.stock.negative_balance',
        TRANSFER_SAME_LOCATION: 'errors.stock.transfer_same_location',
        TRANSFER_INSUFFICIENT: 'errors.stock.transfer_insufficient',
        CONCURRENT_MODIFICATION: 'errors.stock.concurrent_modification',
        WAREHOUSE_DISABLED: 'errors.stock.warehouse_disabled',
        DEFAULT_WAREHOUSE_MUST_BE_ACTIVE: 'errors.stock.default_warehouse_must_be_active',
        DEFAULT_LOCATION_NOT_DELETABLE: 'errors.stock.default_location_not_deletable',
        DEFAULT_LOCATION_NOT_UNSETTABLE: 'errors.stock.default_location_not_unsettable',
        DEFAULT_LOCATION_NOT_DEACTIVATABLE: 'errors.stock.default_location_not_deactivatable',
        DEFAULT_LOCATION_MUST_BE_ACTIVE: 'errors.stock.default_location_must_be_active',
        INVALID_DATE_RANGE: 'errors.stock.invalid_date_range',
    },
    // Inventory session module (conteggi inventario fisico)
    INVENTORY: {
        NOT_FOUND: 'errors.inventory.not_found',
        INVALID_STATUS: 'errors.inventory.invalid_status',
        MISSING_COUNTS: 'errors.inventory.missing_counts',
        WAREHOUSE_ALREADY_COUNTING: 'errors.inventory.warehouse_already_counting',
        WAREHOUSE_NOT_FOUND: 'errors.inventory.warehouse_not_found',
        INVALID_DATE_RANGE: 'errors.inventory.invalid_date_range',
    },
    // Payment Terms module (anagrafica termini di pagamento)
    PAYMENT_TERMS: {
        NOT_FOUND: 'errors.payment_terms.not_found',
        CODE_ALREADY_EXISTS: 'errors.payment_terms.code_already_exists',
        CANNOT_DELETE_SYSTEM_DEFINED: 'errors.payment_terms.cannot_delete_system_defined',
    },
    // Collection Accounts module (anagrafica conti di incasso del tenant)
    COLLECTION_ACCOUNTS: {
        NOT_FOUND: 'errors.collection_accounts.not_found',
        IBAN_ALREADY_EXISTS: 'errors.collection_accounts.iban_already_exists',
    },
    // Numbering Sequences module (sezionali numerazione documenti)
    NUMBERING_SEQUENCES: {
        INVALID_COUNTER_YEAR: 'errors.numbering_sequences.invalid_counter_year',
        INVALID_COUNTER: 'errors.numbering_sequences.invalid_counter',
        COUNTER_CANNOT_DECREASE: 'errors.numbering_sequences.counter_cannot_decrease',
        NOT_FOUND: 'errors.numbering_sequences.not_found',
        NAME_ALREADY_EXISTS: 'errors.numbering_sequences.name_already_exists',
        CANNOT_CHANGE_DOCUMENT_TYPE_IN_USE: 'errors.numbering_sequences.cannot_change_document_type_in_use',
        IN_USE_CANNOT_DELETE: 'errors.numbering_sequences.in_use_cannot_delete',
    },
    // Invoicing Mode (Cassetto Fiscale vs ACube SDI vs None)
    INVOICING_MODE: {
        CONFIRMATION_CHANGED: 'errors.invoicing_mode.confirmation_changed',
        TRANSITION_NOT_AVAILABLE: 'errors.invoicing_mode.transition_not_available',
        ALREADY_IN_MODE: 'errors.invoicing_mode.already_in_mode',
        CASSETTO_FISCALE_NOT_LINKED: 'errors.invoicing_mode.cassetto_fiscale_not_linked',
        ACUBE_BRC_NOT_CONFIGURED: 'errors.invoicing_mode.acube_brc_not_configured',
        ACUBE_FISCAL_ID_ALREADY_USED: 'errors.invoicing_mode.acube_fiscal_id_already_used',
        CASSETTO_FISCALE_MODE_REQUIRED: 'errors.invoicing_mode.cassetto_fiscale_mode_required',
        SDI_INVOICE_SEND_NOT_AVAILABLE: 'errors.invoicing_mode.sdi_invoice_send_not_available',
        NOT_CONTRACTED: 'errors.invoicing_mode.not_contracted',
        DOCUMENT_NOT_OUTGOING: 'errors.invoicing_mode.document_not_outgoing',
        DOCUMENT_ALREADY_SENT: 'errors.invoicing_mode.document_already_sent',
        DOCUMENT_LOCKED_FOR_FISCAL_EDIT: 'errors.invoicing_mode.document_locked_for_fiscal_edit',
        DOCUMENT_INVALID_FOR_SDI: 'errors.invoicing_mode.document_invalid_for_sdi',
        // errorCode per il logging persistito: il servizio di fatturazione ha
        // RIFIUTATO il POST della fattura (nessuna presa in carico). È un esito
        // gestito — l'utente vede il motivo sul documento e può correggere — ma va
        // contato e visto dal backoffice, perché è la prima cosa che si rompe su un
        // tipo documento trasmesso per la prima volta. `warn`, non `error`.
        // Non copre i fallimenti PRIMA della chiamata (riserva commerciale,
        // validazione): quelli non sono un rifiuto del provider.
        SDI_TRANSMISSION_REJECTED: 'errors.invoicing_mode.sdi_transmission_rejected',
    },
    // Reconciliation module
    RECONCILIATION: {
        PROPOSAL_CHANGED: 'errors.reconciliation.proposal_changed',
        LINK_NOT_FOUND: 'errors.reconciliation.link_not_found',
        CASH_MOVEMENT_NOT_FOUND: 'errors.reconciliation.cash_movement_not_found',
        ALREADY_RECONCILED: 'errors.reconciliation.already_reconciled',
        INVALID_LINK_TYPE: 'errors.reconciliation.invalid_link_type',
        NO_MATCHES_FOUND: 'errors.reconciliation.no_matches_found',
        SIMULATION_NOT_ALLOWED: 'errors.reconciliation.simulation_not_allowed',
        FORECAST_ENTITY_REQUIRED: 'errors.reconciliation.forecast_entity_required',
        REALITY_ENTITY_REQUIRED: 'errors.reconciliation.reality_entity_required',
        BACKGROUND_PROPOSAL_FAILED: 'errors.reconciliation.background_proposal_failed',
        AGGREGATE_GROUP_NOT_FOUND: 'errors.reconciliation.aggregate_group_not_found',
        AGGREGATE_GROUP_INVALID: 'errors.reconciliation.aggregate_group_invalid',
        AGGREGATE_GROUP_CHANGED: 'errors.reconciliation.aggregate_group_changed',
        AGGREGATE_GROUP_MIN_MOVEMENTS: 'errors.reconciliation.aggregate_group_min_movements',
        AGGREGATE_GROUP_MOVEMENT_UNAVAILABLE: 'errors.reconciliation.aggregate_group_movement_unavailable',
        AGGREGATE_GROUP_OUT_OF_TOLERANCE: 'errors.reconciliation.aggregate_group_out_of_tolerance',
    },
    // Document Withholding module
    DOCUMENT_WITHHOLDING: {
        NOT_FOUND: 'errors.document_withholding.not_found',
        DOCUMENT_NOT_FOUND: 'errors.document_withholding.document_not_found',
    },
    // Document Social Security module
    DOCUMENT_SOCIAL_SECURITY: {
        NOT_FOUND: 'errors.document_social_security.not_found',
        DOCUMENT_NOT_FOUND: 'errors.document_social_security.document_not_found',
    },
    DOCUMENT_REFERENCES: {
        DOCUMENT_NOT_FOUND: 'errors.document_references.document_not_found',
    },
    // Stripe module
    STRIPE: {
        MISSING_SIGNATURE: 'errors.stripe.missing_signature',
        MISSING_RAW_BODY: 'errors.stripe.missing_raw_body',
        EVENT_ALREADY_PROCESSED: 'errors.stripe.event_already_processed',
        SUBSCRIPTION_NOT_FOUND: 'errors.stripe.subscription_not_found',
        PAYMENT_METHOD_NOT_FOUND: 'errors.stripe.payment_method_not_found',
        NO_ACTIVE_SUBSCRIPTION: 'errors.stripe.no_active_subscription',
        NO_CANCELING_SUBSCRIPTION: 'errors.stripe.no_canceling_subscription',
        SUBSCRIPTION_CREATION_FAILED: 'errors.stripe.subscription_creation_failed',
        MISSING_WEBHOOK_SECRET: 'errors.stripe.missing_webhook_secret',
        FREE_UPGRADE_NOT_SUPPORTED: 'errors.stripe.free_upgrade_not_supported',
        OPERATION_FAILED: 'errors.stripe.operation_failed',
        DISABLED: 'errors.stripe.disabled',
    },
    // Document OCR module
    DOCUMENT_OCR: {
        INVALID_CONTENT_TYPE: 'errors.document_ocr.invalid_content_type',
        NO_FILE_PROVIDED: 'errors.document_ocr.no_file_provided',
        QUEUE_NOT_AVAILABLE: 'errors.document_ocr.queue_not_available',
        EMPTY_FILE: 'errors.document_ocr.empty_file',
        FILE_TOO_LARGE: 'errors.document_ocr.file_too_large',
        PROVIDER_RATE_LIMITED: 'errors.document_ocr.provider_rate_limited',
        PROVIDER_UNAVAILABLE: 'errors.document_ocr.provider_unavailable',
    },
    /**
     * Pagina Monitoraggio del backoffice (code BullMQ e Redis).
     * `QUEUES_DISABLED`: `REDIS_URL` assente, nessuna coda da mostrare né
     * su cui agire; `REDIS_UNAVAILABLE`: Redis configurato ma non raggiungibile.
     */
    SYSTEM_MONITOR: {
        QUEUES_DISABLED: 'errors.system_monitor.queues_disabled',
        REDIS_UNAVAILABLE: 'errors.system_monitor.redis_unavailable',
        JOB_NOT_FOUND: 'errors.system_monitor.job_not_found',
        JOB_NOT_FAILED: 'errors.system_monitor.job_not_failed',
        QUEUE_NOT_FOUND: 'errors.system_monitor.queue_not_found',
    },
    // Error Logs (backoffice)
    ERROR_LOGS: {
        CATEGORY_HAS_UNRESOLVED: 'errors.error_logs.category_has_unresolved',
    },
    // Foreign Invoice Reconciliation module
    FOREIGN_INVOICE: {
        INVALID_CONTENT_TYPE: 'errors.foreign_invoice.invalid_content_type',
        INVALID_MONTH_FORMAT: 'errors.foreign_invoice.invalid_month_format',
        TENANT_FISCAL_DATA_INVALID: 'errors.foreign_invoice.tenant_fiscal_data_invalid',
        EMPTY_VENDOR_DESCRIPTION: 'errors.foreign_invoice.empty_vendor_description',
        SEED_NOT_FOUND: 'errors.foreign_invoice.seed_not_found',
        SEED_ALIAS_INVALID: 'errors.foreign_invoice.seed_alias_invalid',
        SEED_ALIAS_DUPLICATE: 'errors.foreign_invoice.seed_alias_duplicate',
        VENDOR_COUNTRY_INVALID: 'errors.foreign_invoice.vendor_country_invalid',
        VENDOR_COUNTRY_MISSING: 'errors.foreign_invoice.vendor_country_missing',
    },
    // Invoice Advance module
    INVOICE_ADVANCE: {
        OPERATION_NOT_DISBURSED: 'errors.invoice_advance.operation_not_disbursed',
    },
    // Plans module
    PLANS: {
        NOT_FOUND: 'errors.plans.not_found',
        CODE_ALREADY_EXISTS: 'errors.plans.code_already_exists',
        NAME_ALREADY_EXISTS: 'errors.plans.name_already_exists',
        NOT_SELECTABLE: 'errors.plans.not_selectable',
    },
    // Backoffice «Costi e piani»: contratti fornitori, voci di costo, costi
    // interni e offerta alternativa.
    INFRASTRUCTURE_COSTS: {
        PDF_REQUIRED: 'errors.infrastructure_costs.pdf_required',
        INVALID_PDF: 'errors.infrastructure_costs.invalid_pdf',
        PDF_TOO_LARGE: 'errors.infrastructure_costs.pdf_too_large',
        SUPPLIER_AND_TITLE_REQUIRED: 'errors.infrastructure_costs.supplier_and_title_required',
        CONTRACT_NOT_FOUND: 'errors.infrastructure_costs.contract_not_found',
        CONTRACT_STILL_ACTIVE: 'errors.infrastructure_costs.contract_still_active',
        /** Il contratto esiste ma il suo PDF non è (più) nello storage. */
        CONTRACT_FILE_NOT_FOUND: 'errors.infrastructure_costs.contract_file_not_found',
        COST_ITEM_NOT_FOUND: 'errors.infrastructure_costs.cost_item_not_found',
        /** La soglia critica deve superare quella di avviso. */
        INVALID_USAGE_THRESHOLDS: 'errors.infrastructure_costs.invalid_usage_thresholds',
        /** Da uno a sei piani nell'offerta alternativa. */
        OFFER_PLAN_COUNT: 'errors.infrastructure_costs.offer_plan_count',
        INVALID_OFFER: 'errors.infrastructure_costs.invalid_offer',
    },
    // Tenant Settings module
    TENANT_SETTINGS: {
        NOT_FOUND: 'errors.tenant_settings.not_found',
        /** «Pubblica automaticamente all'approvazione»: solo chi pubblica i rapportini. */
        WORK_REPORT_AUTO_PUBLISH_FORBIDDEN: 'errors.tenant_settings.work_report_auto_publish_forbidden',
        /** «Firma del cliente obbligatoria»: solo chi approva rapportini o verifiche. */
        SIGNATURE_REQUIREMENT_FORBIDDEN: 'errors.tenant_settings.signature_requirement_forbidden',
    },
    // Strumenti di sviluppo (solo local/dev)
    DEV_TOOLS: {
        NOT_AVAILABLE: 'errors.dev_tools.not_available',
    },
    FEATURE_FLAGS: {
        DEPENDENCY_REQUIRED: 'errors.feature_flags.dependency_required',
    },
    // Cassetto Fiscale module
    CASSETTO_FISCALE: {
        APPOINTEE_FISCAL_CODE_NOT_CONFIGURED: 'errors.cassetto_fiscale.appointee_fiscal_code_not_configured',
        ALREADY_HAS_APPOINTEE: 'errors.cassetto_fiscale.already_has_appointee',
        CREDENTIAL_SUBMISSION_BLOCKED: 'errors.cassetto_fiscale.credential_submission_blocked',
        NO_APPOINTEE_LINKED: 'errors.cassetto_fiscale.no_appointee_linked',
        ACUBE_DELEGA_NOT_FOUND: 'errors.cassetto_fiscale.acube_delega_not_found',
        ACUBE_DELEGA_ALREADY_ACTIVE: 'errors.cassetto_fiscale.acube_delega_already_active',
        ACUBE_DELEGA_NOT_ACTIVE: 'errors.cassetto_fiscale.acube_delega_not_active',
        CREDENTIALS_OR_DELEGATION_REQUIRED: 'errors.cassetto_fiscale.credentials_or_delegation_required',
        // Lo scarico automatico non si spegne a mano quando è una delega a
        // governarlo: si disattiva la delega, che spegne anche lo schedule.
        SCHEDULE_MANAGED_BY_DELEGATION: 'errors.cassetto_fiscale.schedule_managed_by_delegation',
        // Il provider ha rifiutato le credenziali Fisconline inserite (qualunque
        // 4xx sulla PUT): esito atteso di un'azione utente, non un guasto.
        CREDENTIALS_REJECTED: 'errors.cassetto_fiscale.credentials_rejected',
        // ACube ha rifiutato (400/422) le credenziali Fisconline dell'INCARICATO
        // inserite dall'admin nel backoffice: esito atteso, non un guasto.
        APPOINTEE_CREDENTIALS_REJECTED: 'errors.cassetto_fiscale.appointee_credentials_rejected',
        // errorCode per il logging persistito (skip sync / cron scadenza credenziali)
        CREDENTIALS_EXPIRED: 'errors.cassetto_fiscale.credentials_expired',
        CREDENTIALS_CHECK_FAILED: 'errors.cassetto_fiscale.credentials_check_failed',
        CREDENTIALS_EMAIL_FAILED: 'errors.cassetto_fiscale.credentials_email_failed',
        // errorCode per il logging persistito: fallimento INATTESO (non il 400
        // AdE-pending) dell'attivazione schedule nel retry della delega A-Cube
        ACUBE_SCHEDULE_ACTIVATION_FAILED: 'errors.cassetto_fiscale.acube_schedule_activation_failed',
        // errorCode per il logging persistito: spegnimento best-effort dello
        // schedule remoto orfano (disattivazione concorrente vs cron) fallito
        ACUBE_SCHEDULE_ORPHAN_CLEANUP_FAILED: 'errors.cassetto_fiscale.acube_schedule_orphan_cleanup_failed',
        // errorCode per il logging persistito: dopo nuove credenziali Fisconline
        // il rinnovo automatico dello schedule fatture non è stato riacceso
        // (best-effort: le credenziali restano salvate)
        ACUBE_SCHEDULE_AUTO_RENEW_RESTORE_FAILED: 'errors.cassetto_fiscale.acube_schedule_auto_renew_restore_failed',
        // 409 dello scarico storico chiesto A MANO dal backoffice: senza data di
        // partenza non esiste un confine da cui scaricare, e una data nel futuro
        // descriverebbe un periodo vuoto. Nei flussi AUTOMATICI gli stessi due
        // casi non sono errori: l'attivazione prosegue e il job viene saltato.
        DELEGATION_START_DATE_REQUIRED: 'errors.cassetto_fiscale.delegation_start_date_required',
        DELEGATION_START_DATE_IN_FUTURE: 'errors.cassetto_fiscale.delegation_start_date_in_future',
        // errorCode per il logging persistito: nei flussi automatici lo scarico
        // storico è best-effort e non deve far fallire l'attivazione. Le due
        // chiavi distinguono "non c'erano le condizioni" (skip atteso) da
        // "il provider ha rifiutato la creazione del job" (guasto).
        ACUBE_HISTORY_JOB_SKIPPED: 'errors.cassetto_fiscale.acube_history_job_skipped',
        ACUBE_HISTORY_JOB_FAILED: 'errors.cassetto_fiscale.acube_history_job_failed',
    },
    // ACube Module - External API integration
    ACUBE: {
        UPSTREAM_ERROR: 'errors.acube.upstream_error',
        NOT_FOUND: 'errors.acube.not_found',
        INVALID_ID: 'errors.acube.invalid_id',
        OPERATION_FAILED: 'errors.acube.operation_failed',
        AUTH_FAILED: 'errors.acube.auth_failed',
        // errorCode per il logging persistito: job del cassetto fiscale scartato
        // dalla mappatura strict (tipo/stato nuovi lato ACube, data illeggibile)
        // o fallito lato ACube. Non arrivano mai all'utente.
        JOB_TYPE_UNKNOWN: 'errors.acube.job_type_unknown',
        JOB_STATUS_UNKNOWN: 'errors.acube.job_status_unknown',
        JOB_DATE_UNPARSEABLE: 'errors.acube.job_date_unparseable',
        JOB_FAILED: 'errors.acube.job_failed',
        // errorCode per il logging persistito: la business registry esisteva già
        // ma la GET by fiscal id non l'ha vista (ACube risponde 404 anche a
        // registry esistente) e la create che ne è seguita è stata rifiutata.
        // L'esito è GESTITO — l'utente non subisce nulla — ma va contato: con il
        // campo `acube*RegistryEnsuredAt` la create parte una volta sola per
        // tenant, quindi ogni riga è un evento singolo e significativo.
        REGISTRY_EXISTS_UNSEEN_BY_LOOKUP: 'errors.acube.registry_exists_unseen_by_lookup',
        // errorCode per il logging persistito: il pull del cassetto non ha
        // scaricato le fatture ricevute di una P.IVA aggiuntiva del tenant. La
        // P.IVA principale prosegue e le fatture restano `downloaded=false` per il
        // giro successivo; una riga aperta per tenant e P.IVA. Non arriva mai
        // all'utente.
        ADDITIONAL_VAT_ID_PULL_FAILED: 'errors.acube.additional_vat_id_pull_failed',
        // F24 dal cassetto — mapper: errorCode per il logging persistito degli
        // scarti della mappatura strict di `GET /f24-forms`. Un modello che non
        // sappiamo interpretare non finisce a DB con valori plausibili: viene
        // scartato e contato. Non arrivano mai all'utente.
        F24_FORM_STATUS_UNKNOWN: 'errors.acube.f24_form_status_unknown',
        F24_FORM_DATE_UNPARSEABLE: 'errors.acube.f24_form_date_unparseable',
        F24_FORM_AMOUNT_INVALID: 'errors.acube.f24_form_amount_invalid',
        F24_FORM_PROTOCOL_MISSING: 'errors.acube.f24_form_protocol_missing',
        // Specchio dei pagamenti Open Banking (`AcubePayment`) — mapper:
        // errorCode per il logging persistito. A differenza dei job, un valore non
        // riconosciuto NON scarta il pagamento: il campo resta null, la riga si
        // scrive comunque e non produce transizioni di stato.
        PAYMENT_STATUS_UNKNOWN: 'errors.acube.payment_status_unknown',
        PAYMENT_DIRECTION_UNKNOWN: 'errors.acube.payment_direction_unknown',
        PAYMENT_DATE_UNPARSEABLE: 'errors.acube.payment_date_unparseable',
        // Webhook: `success` e `payment.status` dicono cose diverse (fa fede
        // `success`).
        PAYMENT_STATUS_CONFLICT: 'errors.acube.payment_status_conflict',
        // Pull: elemento della collection senza uuid, non registrabile.
        PAYMENT_UUID_MISSING: 'errors.acube.payment_uuid_missing',
        // Rilettura puntuale di un pagamento che non esiste per il tenant.
        PAYMENT_NOT_FOUND: 'errors.acube.payment_not_found',
        // Rilettura puntuale: ACube risponde 404, il provider non conosce il
        // pagamento (per il reset admin vale come "sconosciuto al provider").
        PAYMENT_REMOTE_NOT_FOUND: 'errors.acube.payment_remote_not_found',
        // Sync manuale dal backoffice di un tenant senza conti ACube o con la
        // business registry Open Banking mai verificata: leggere i pagamenti la
        // creerebbe (`ensureCompanyExists`).
        PAYMENTS_SYNC_NOT_AVAILABLE: 'errors.acube.payments_sync_not_available',
        // F24 dal cassetto — sync: errorCode per il logging persistito del giro di
        // sync. `F24_PDF_STORE_FAILED` è il guasto INATTESO dell'archiviazione di
        // un PDF (i ritardi dell'AdE — 409/422 sul modello, quietanza non ancora
        // pubblicata — sono esiti normali e non si loggano); `F24_STORAGE_DISABLED`
        // è lo storage non configurato, un warn per giro e gli specchi proseguono;
        // `F24_REFRESH_FAILED` è il guasto INATTESO della rilettura di un singolo
        // modello (`getForm`): la riga resta nella selezione e si ritenta al giro
        // dopo, le altre righe proseguono; `F24_SYNC_STEP_FAILED` è un singolo
        // passo del giro caduto (il giro prosegue: distinto dalla singola riga —
        // PDF non archiviato o modello non riletto). Il giro INTERROTTO non ha un
        // codice proprio: il passo del cron accumula il guasto e la riga la scrive
        // il riepilogo del sync tenant, che è il livello che ne decide l'esito.
        F24_PDF_STORE_FAILED: 'errors.acube.f24_pdf_store_failed',
        F24_REFRESH_FAILED: 'errors.acube.f24_refresh_failed',
        F24_STORAGE_DISABLED: 'errors.acube.f24_storage_disabled',
        F24_SYNC_STEP_FAILED: 'errors.acube.f24_sync_step_failed',
        // F24 dal cassetto — limite annuo dell'opzione contrattuale
        // `F24_BULK_DOWNLOAD` raggiunto: il passo 1 smette di importare righe
        // NUOVE per quel tenant e lo dichiara una volta per giro (il summary
        // porta `skippedForLimit`). Non arriva all'utente: le righe saltate
        // rientrano da sole al giro dopo, perché la finestra di lettura si deduce
        // dalle righe a DB e quelle non importate non la fanno avanzare.
        F24_DOWNLOAD_LIMIT_REACHED: 'errors.acube.f24_download_limit_reached',
        // errorCode per il logging persistito dell'allineamento dello scarico F24
        // al contratto (`AcubeF24EntitlementService.alignScheduleWithEntitlement`):
        // un guasto ACube non deve far fallire la firma del contratto né il
        // collegamento del cassetto, quindi si logga e non si propaga. Il 402 del
        // servizio non acquistato NON usa questo codice: è
        // `F24_SERVICE_NOT_ACTIVE`, un fatto commerciale nostro su ACube.
        F24_ENTITLEMENT_ALIGN_FAILED: 'errors.acube.f24_entitlement_align_failed',
        // F24 dal cassetto — match: errorCode per il logging persistito della
        // scala di match di una SINGOLA riga (`docs/F24_CASSETTO_FISCALE.md` §4.2).
        // Ogni riga ha la sua transazione corta: un guasto lascia quella riga da
        // valutare al giro dopo e il passo continua con le altre — distinto da
        // `F24_SYNC_STEP_FAILED`, che è il passo intero caduto. Non arriva
        // all'utente.
        F24_MATCH_FAILED: 'errors.acube.f24_match_failed',
        // F24 dal cassetto — revisione: queste ARRIVANO all'utente. Sono gli esiti
        // della coda "da verificare" (`/tenants/:tenantId/acube-f24-forms`): il
        // modello non è del tenant (404), non chiede più una decisione perché un
        // altro l'ha già presa (409), la coppia documento+scadenza scelta non è più
        // un candidato valido (422) e i due PDF non ancora nel nostro archivio
        // (404). Il 422 è un caso normale, non un abuso del client: fra il match e
        // la revisione l'impegno può essere stato pagato, cancellato o già coperto
        // da un'altra evidenza del cassetto.
        F24_FORM_NOT_FOUND: 'errors.acube.f24_form_not_found',
        F24_FORM_NOT_PENDING_REVIEW: 'errors.acube.f24_form_not_pending_review',
        F24_CANDIDATE_INVALID: 'errors.acube.f24_candidate_invalid',
        F24_FORM_PDF_NOT_AVAILABLE: 'errors.acube.f24_form_pdf_not_available',
        F24_RECEIPT_PDF_NOT_AVAILABLE: 'errors.acube.f24_receipt_pdf_not_available',
        // 409 del job F24 su periodo chiesto dal backoffice: il 402 di ACube dice
        // che il servizio F24 non è acquistato sull'account. Nei flussi automatici
        // lo stesso 402 è un esito atteso e resta un debug (fail-soft, §4.10); qui
        // un operatore ha premuto un pulsante e deve sapere perché non è partito.
        F24_SERVICE_NOT_ACTIVE: 'errors.acube.f24_service_not_active',
        // 409 delle operazioni F24 del backoffice (job su periodo, sync a
        // richiesta) quando l'azienda NON ha l'opzione contrattuale di scarico
        // massivo F24. È il gemello commerciale di `F24_SERVICE_NOT_ACTIVE`, ma
        // guarda dalla parte opposta: là il servizio non è acquistato da NOI su
        // ACube, qui non è stato venduto al CLIENTE. Lo scarico costa, quindi
        // senza contratto non si scarica: la via è vendere l'opzione, non premere
        // un pulsante.
        F24_DOWNLOAD_NOT_INCLUDED: 'errors.acube.f24_download_not_included',
        // 400 del job su periodo del backoffice: il job type nel path non è uno
        // dei due che il cassetto offre all'operatore (`invoice-download`,
        // `f24-download`). Gli altri job type di ACube — report e zip — non hanno
        // un mirror da noi e non si scaricano da quella superficie.
        JOB_TYPE_NOT_ALLOWED: 'errors.acube.job_type_not_allowed',
    },
    // ACube Webhook Module
    ACUBE_WEBHOOK: {
        MISSING_SIGNATURE: 'errors.acube_webhook.missing_signature',
        MISSING_CONTENT_DIGEST: 'errors.acube_webhook.missing_content_digest',
        INVALID_CONTENT_DIGEST: 'errors.acube_webhook.invalid_content_digest',
        INVALID_SIGNATURE: 'errors.acube_webhook.invalid_signature',
        INVALID_PAYLOAD: 'errors.acube_webhook.invalid_payload',
        // La chiave pubblica ACube non si scarica e non ce n'è una in cache: la
        // firma non è giudicabile. Vale 503 atteso (warn), così ACube ritenta;
        // diverso da INVALID_SIGNATURE, che è un rifiuto definitivo (401).
        PUBLIC_KEY_UNAVAILABLE: 'errors.acube_webhook.public_key_unavailable',
        // errorCode per il logging persistito degli eventi webhook ACube
        // (`AcubeWebhookService`): una chiave per ESITO, non per evento — quale
        // evento fosse sta in `metadata.eventType`. Il registro errori filtra su
        // queste chiavi, niente stringhe inline nei log.
        //
        // Il job fallito NON è qui: gli eventi `job`/`job-error` si limitano a
        // registrare il job, e la riga del guasto la scrive il registro con
        // `ERROR_KEYS.ACUBE.JOB_FAILED` — un solo codice per guasto, sulla sola
        // transizione verso ERROR (le redelivery non moltiplicano le righe).
        INVALID_JSON: 'errors.acube_webhook.invalid_json',
        UNKNOWN_EVENT: 'errors.acube_webhook.unknown_event',
        PAYLOAD_INCOMPLETE: 'errors.acube_webhook.payload_incomplete',
        TENANT_NOT_FOUND: 'errors.acube_webhook.tenant_not_found',
        DOCUMENT_NOT_FOUND: 'errors.acube_webhook.document_not_found',
        // Il documento è già sotto lock advisory (invio in corso, o un altro
        // webhook): non è un guasto ed è diverso da "non trovato". Vale 503, così
        // ACube ritenta e il registro errori non lo confonde con un'associazione
        // mancante.
        DOCUMENT_BUSY: 'errors.acube_webhook.document_busy',
        UNKNOWN_MARKING: 'errors.acube_webhook.unknown_marking',
        PAYMENT_FAILED: 'errors.acube_webhook.payment_failed',
        INVOICE_IMPORT_FAILED: 'errors.acube_webhook.invoice_import_failed',
        // La prenotazione del contatore commerciale ha perso la corsa con un
        // import concorrente dello stesso tenant anche dopo i tentativi: non è un
        // guasto. 503 con esito previsto, così ACube ritenta il webhook.
        INVOICE_IMPORT_CONFLICT: 'errors.acube_webhook.invoice_import_conflict',
        GET_INVOICE_FAILED: 'errors.acube_webhook.get_invoice_failed',
        MARK_DOWNLOADED_FAILED: 'errors.acube_webhook.mark_downloaded_failed',
        RECEIPT_EMAIL_FAILED: 'errors.acube_webhook.receipt_email_failed',
        // Esiti negativi del SDI comunicati per webhook. Il writer resta puro e
        // restituisce l'esito: la riga la scrive `AcubeWebhookService`, che è il
        // livello che decide. `warn` e solo sulla TRANSIZIONE di marking — come per
        // `ACUBE.JOB_FAILED`, le redelivery del provider non devono moltiplicare le
        // righe. I codici dei controlli SDI falliti stanno in `metadata.codes`.
        SDI_INVOICE_REJECTED: 'errors.acube_webhook.sdi_invoice_rejected',
        SDI_INVOICE_ERROR: 'errors.acube_webhook.sdi_invoice_error',
        SDI_INVOICE_QUARANTINED: 'errors.acube_webhook.sdi_invoice_quarantined',
        // Esiti del percorso di CONSEGNA: per il fisco la fattura esiste (il
        // marking resta `SENT`), ma richiedono un'azione commerciale. Non passano
        // da `stateChanged`, che guarda il marking mappato: la riga si scrive alla
        // prima registrazione del marking GREZZO del provider.
        SDI_INVOICE_REJECTED_BY_PA: 'errors.acube_webhook.sdi_invoice_rejected_by_pa',
        SDI_INVOICE_NOT_DELIVERED: 'errors.acube_webhook.sdi_invoice_not_delivered',
    },
    // CGN Fintech - Servizio di pagamento F24 (`/f24v2`)
    CGN_F24: {
        // Feature dietro feature flag di tenant (`f24PaymentsEnabled`, default OFF):
        // il tenant non ha il pagamento F24 attivo
        FEATURE_DISABLED: 'errors.cgn_f24.feature_disabled',
        NOT_CONFIGURED: 'errors.cgn_f24.not_configured',
        AUTH_FAILED: 'errors.cgn_f24.auth_failed',
        UPSTREAM_ERROR: 'errors.cgn_f24.upstream_error',
        NOT_FOUND: 'errors.cgn_f24.not_found',
        INVALID_DATE_FORMAT: 'errors.cgn_f24.invalid_date_format',
        // errorCode per il logging persistito delle chiamate fallite
        REQUEST_FAILED: 'errors.cgn_f24.request_failed',
        // --- Import (acquisizione da PDF) ---
        INVALID_CONTENT_TYPE: 'errors.cgn_f24.invalid_content_type',
        NO_FILE_PROVIDED: 'errors.cgn_f24.no_file_provided',
        INVALID_FILE_TYPE: 'errors.cgn_f24.invalid_file_type',
        FILE_TOO_LARGE: 'errors.cgn_f24.file_too_large',
        TOO_MANY_FILES: 'errors.cgn_f24.too_many_files',
        TOO_MANY_PAGES: 'errors.cgn_f24.too_many_pages',
        EXTRACTION_FAILED: 'errors.cgn_f24.extraction_failed',
        EXTRACTION_TIMEOUT: 'errors.cgn_f24.extraction_timeout',
        // Upload identico a una delega già acquisita e non cancellata
        DUPLICATE: 'errors.cgn_f24.duplicate',
        // Stesso modello ripetuto DENTRO il caricamento corrente: vince la prima
        // occorrenza, le successive sono copie da scartare (non un errore utente)
        DUPLICATE_IN_BATCH: 'errors.cgn_f24.duplicate_in_batch',
        // Stesso file, byte per byte, già caricato in precedenza: riconosciuto
        // PRIMA dell'estrattore (nessuna delega remota creata)
        DUPLICATE_FILE: 'errors.cgn_f24.duplicate_file',
        // Lo stesso modello è una bozza ancora giovane di un ALTRO utente: non si
        // ritira (potrebbe essere una revisione viva), l'import viene respinto
        DRAFT_IN_REVIEW_BY_OTHER: 'errors.cgn_f24.draft_in_review_by_other',
        // Delega interamente compensata: va presentata sui canali dell'AdE
        ZERO_BALANCE: 'errors.cgn_f24.zero_balance',
        // Errore inatteso: il dettaglio tecnico resta nei log, al client va un
        // messaggio generico (mai messaggi interni)
        IMPORT_FAILED_UNEXPECTED: 'errors.cgn_f24.import_failed_unexpected',
        // Archiviazione del PDF sorgente non riuscita: senza il file archiviato la
        // delega non si crea (invariante "delega locale ⇔ PDF archiviato"), e
        // quella appena creata sull'istituto viene ritirata
        SOURCE_PDF_NOT_STORED: 'errors.cgn_f24.source_pdf_not_stored',
        // La delega non ha un PDF sorgente da mostrare (nata da JSON, o anteriore
        // all'archiviazione)
        SOURCE_PDF_NOT_AVAILABLE: 'errors.cgn_f24.source_pdf_not_available',
        // Degrado sincrono (senza coda): troppe pagine in una sola richiesta
        TOO_MANY_SYNC_PAGES: 'errors.cgn_f24.too_many_sync_pages',
        QUEUE_NOT_AVAILABLE: 'errors.cgn_f24.queue_not_available',
        JOB_NOT_FOUND: 'errors.cgn_f24.job_not_found',
        // errorCode per il logging persistito dei job di import falliti
        IMPORT_JOB_FAILED: 'errors.cgn_f24.import_job_failed',
        // --- Revisione e ciclo di vita locale ---
        UNSUPPORTED_DELEGATION_TYPE: 'errors.cgn_f24.unsupported_delegation_type',
        INVALID_PAYLOAD: 'errors.cgn_f24.invalid_payload',
        // I totali dichiarati non tornano con la somma delle righe tributo
        PAYLOAD_ARITHMETIC_MISMATCH: 'errors.cgn_f24.payload_arithmetic_mismatch',
        NOT_EDITABLE: 'errors.cgn_f24.not_editable',
        NOT_CONFIRMABLE: 'errors.cgn_f24.not_confirmable',
        // La bozza è stata ritirata sotto i piedi (sostituita da un nuovo
        // caricamento, o spazzata come orfana): non è uno stato da correggere, la
        // strada è ricaricare il PDF
        DRAFT_RETIRED: 'errors.cgn_f24.draft_retired',
        ALREADY_CONFIRMED: 'errors.cgn_f24.already_confirmed',
        // Delega già confermata: saldo e data si propagano a documento e scadenza
        // (solo se il documento è nato dalla conferma) finché la scadenza non è
        // pagata o riconciliata
        CONFIRMED_SCHEDULE_SETTLED: 'errors.cgn_f24.confirmed_schedule_settled',
        // Il modello su CGN dichiara saldo o data diversi da documento e scadenza:
        // consolidare o pagare farebbe addebitare un importo diverso
        CONFIRMED_AMOUNTS_MISALIGNED: 'errors.cgn_f24.confirmed_amounts_misaligned',
        // Stesso caso su una delega collegata a un documento esistente: il
        // riferimento è il saldo approvato al collegamento, non la rata
        CONFIRMED_AMOUNTS_MISALIGNED_LINKED: 'errors.cgn_f24.confirmed_amounts_misaligned_linked',
        // Delega già consolidata: su CGN non si corregge più, «Rivedi» non serve
        CONFIRMED_AMOUNTS_MISALIGNED_CONSOLIDATED: 'errors.cgn_f24.confirmed_amounts_misaligned_consolidated',
        NOT_DELETABLE: 'errors.cgn_f24.not_deletable',
        DOCUMENT_NOT_FOUND: 'errors.cgn_f24.document_not_found',
        DOCUMENT_NOT_TAX_PAYMENT: 'errors.cgn_f24.document_not_tax_payment',
        DOCUMENT_SCHEDULE_NOT_FOUND: 'errors.cgn_f24.document_schedule_not_found',
        SCHEDULE_ALREADY_HAS_ACTIVE_DELEGATION: 'errors.cgn_f24.schedule_already_has_active_delegation',
        SCHEDULE_ALREADY_PAID: 'errors.cgn_f24.schedule_already_paid',
        // --- Pagamento (consolidamento, mandato, dispositiva) ---
        // La delega non è nello stato richiesto dall'operazione
        NOT_CONSOLIDATABLE: 'errors.cgn_f24.not_consolidatable',
        NOT_CONSOLIDATED: 'errors.cgn_f24.not_consolidated',
        NOT_PAYABLE: 'errors.cgn_f24.not_payable',
        // Il pagamento richiede la scadenza creata alla conferma della revisione
        NOT_SCHEDULED: 'errors.cgn_f24.not_scheduled',
        // Mandato di addebito non ancora firmato
        CONSENT_REQUIRED: 'errors.cgn_f24.consent_required',
        // Dispositiva già inviata per questa delega
        PAYMENT_ALREADY_SENT: 'errors.cgn_f24.payment_already_sent',
        // Sorgente dell'IBAN: esattamente uno tra conto e IBAN manuale
        PAYMENT_SOURCE_REQUIRED: 'errors.cgn_f24.payment_source_required',
        ACCOUNT_NOT_FOUND: 'errors.cgn_f24.account_not_found',
        ACCOUNT_IBAN_MISSING: 'errors.cgn_f24.account_iban_missing',
        INVALID_IBAN: 'errors.cgn_f24.invalid_iban',
        // Finestra di invio della dispositiva (doc §7.1)
        PAYMENT_DATE_IN_THE_PAST: 'errors.cgn_f24.payment_date_in_the_past',
        PAYMENT_DATE_TOO_FAR: 'errors.cgn_f24.payment_date_too_far',
        PAYMENT_DATE_NOT_WORKING_DAY: 'errors.cgn_f24.payment_date_not_working_day',
        SAME_DAY_CUTOFF_PASSED: 'errors.cgn_f24.same_day_cutoff_passed',
        // Esito della dispositiva ignoto (guasto di rete/upstream dopo l'invio):
        // lo stato reale lo determina la riconciliazione
        PAYMENT_OUTCOME_UNKNOWN: 'errors.cgn_f24.payment_outcome_unknown',
        // --- Riprova su versamento fallito ---
        // Stato da cui la riprova non ha senso (bozza, in carico, ...)
        NOT_RETRYABLE: 'errors.cgn_f24.not_retryable',
        // Il versamento risulta RIUSCITO: tipicamente lo scopre il pre-flight
        // della riprova (fallimento locale stantio, risolto a mano dall'istituto).
        // Messaggio dedicato: "non ritentabile" suonerebbe come un problema.
        RETRY_ALREADY_PAID: 'errors.cgn_f24.retry_already_paid',
        // Termine di invio superato: non si ricrea, si regolarizza col ravvedimento
        RETRY_EXPIRED_NEEDS_RAVVEDIMENTO: 'errors.cgn_f24.retry_expired_needs_ravvedimento',
        // L'esito del versamento non è mai arrivato ed è ancora in verifica presso
        // l'istituto: verdetto PROVVISORIO, la delega può ancora risultare pagata.
        // Ripagare qui produrrebbe un doppio versamento.
        RETRY_OUTCOME_PENDING: 'errors.cgn_f24.retry_outcome_pending',
        // --- Anomalie di cassa (solo log + notifica interna, mai risposte HTTP) ---
        // Due deleghe PAGATE sulla stessa scadenza: il tributo è stato versato due
        // volte. Rilevamento best-effort al passaggio a PAGATA.
        DOUBLE_PAYMENT_DETECTED: 'errors.cgn_f24.double_payment_detected',
        // --- Esiti persistiti sulla scadenza (`paymentErrorMessage`) ---
        // Ogni errore successivo alla transazione comporta lo STORNO dell'addebito
        PAYMENT_FAILED_REVERSED: 'errors.cgn_f24.payment_failed_reversed',
        PAYMENT_EXPIRED: 'errors.cgn_f24.payment_expired',
        // --- Webhook notifiche ---
        WEBHOOK_INVALID_PAYLOAD: 'errors.cgn_f24.webhook_invalid_payload',
        WEBHOOK_SIGNATURE_INVALID: 'errors.cgn_f24.webhook_signature_invalid',
        // errorCode per il logging persistito delle notifiche non elaborabili
        WEBHOOK_PROCESSING_FAILED: 'errors.cgn_f24.webhook_processing_failed',
        // errorCode per il logging persistito delle notifiche con `stato_delega`
        // non riconosciuto (sospetto: stato sintetico al posto di quello interno)
        WEBHOOK_UNKNOWN_STATE: 'errors.cgn_f24.webhook_unknown_state',
        // errorCode per il logging persistito della riconciliazione periodica
        RECONCILIATION_FAILED: 'errors.cgn_f24.reconciliation_failed',
    },
    // Fabrick Module - External API integration
    FABRICK: {
        UPSTREAM_ERROR: 'errors.fabrick.upstream_error',
        NOT_FOUND: 'errors.fabrick.not_found',
        OPERATION_FAILED: 'errors.fabrick.operation_failed',
        AUTH_FAILED: 'errors.fabrick.auth_failed',
        NOT_CONFIGURED: 'errors.fabrick.not_configured',
        PSU_DATA_INCOMPLETE: 'errors.fabrick.psu_data_incomplete',
        // Rollback del provider bancario verso ACube (backoffice)
        ROLLBACK_DETACH_REQUIRED: 'errors.fabrick.rollback_detach_required',
        // errorCode per il logging persistito dei fallimenti per-conto
        ROLLBACK_SWITCH_FAILED: 'errors.fabrick.rollback_switch_failed',
        // errorCode per il logging persistito: `pispStatus`/`workflowStatus` fuori
        // dalla union nota. Resta PENDING: uno stato sconosciuto non muove mai una
        // transizione del bonifico.
        PAYMENT_STATUS_UNKNOWN: 'errors.fabrick.payment_status_unknown',
        // errorCode per il logging persistito: pagamento in `REQUIRED_ACTION`.
        // Valore storico (non `errors.*`): le righe già nel registro errori lo
        // usano, e i filtri del backoffice restano validi.
        PAYMENT_REQUIRED_ACTION: 'FABRICK_PAYMENT_REQUIRED_ACTION',
    },
    // Email Module
    EMAIL: {
        SEND_FAILED: 'errors.email.send_failed',
        MAIL_SUPPORT_NOT_CONFIGURED: 'errors.email.mail_support_not_configured',
    },
    // FatturaPA Import module
    FATTURAPA_IMPORT: {
        INVALID_FISCAL_AMOUNT: 'errors.fatturapa_import.invalid_fiscal_amount',
        INVALID_PAYMENT_AMOUNT: 'errors.fatturapa_import.invalid_payment_amount',
        MULTIPLE_BODIES_UNSUPPORTED: 'errors.fatturapa_import.multiple_bodies_unsupported',
        INVALID_XML: 'errors.fatturapa_import.invalid_xml',
        MISSING_REQUIRED_FIELD: 'errors.fatturapa_import.missing_required_field',
        INVALID_DIRECTION: 'errors.fatturapa_import.invalid_direction',
        DUPLICATE_DOCUMENT: 'errors.fatturapa_import.duplicate_document',
        TENANT_VAT_NOT_CONFIGURED: 'errors.fatturapa_import.tenant_vat_not_configured',
        DOCUMENT_NOT_BELONGS_TO_TENANT: 'errors.fatturapa_import.document_not_belongs_to_tenant',
        VALIDATION_ERRORS: 'errors.fatturapa_import.validation_errors',
        INVALID_XML_ROOT_NOT_FOUND: 'errors.fatturapa_import.invalid_xml_root_not_found',
        NOT_A_FATTURA: 'errors.fatturapa_import.not_a_fattura',
        INVALID_XML_FORMAT: 'errors.fatturapa_import.invalid_xml_format',
        INVALID_XML_HEADER_NOT_FOUND: 'errors.fatturapa_import.invalid_xml_header_not_found',
        INVALID_XML_BODY_NOT_FOUND: 'errors.fatturapa_import.invalid_xml_body_not_found',
        INVALID_STRUCTURE: 'errors.fatturapa_import.invalid_structure',
        REQUIRED_DATE_MISSING: 'errors.fatturapa_import.required_date_missing',
        INVALID_DATE_FORMAT: 'errors.fatturapa_import.invalid_date_format',
        CANNOT_DETECT_DIRECTION_NO_VAT: 'errors.fatturapa_import.cannot_detect_direction_no_vat',
        CANNOT_DETECT_DIRECTION_NO_MATCH: 'errors.fatturapa_import.cannot_detect_direction_no_match',
        INVALID_CONTENT_TYPE: 'errors.fatturapa_import.invalid_content_type',
        NO_FILES_PROVIDED: 'errors.fatturapa_import.no_files_provided',
        MAX_FILES_EXCEEDED: 'errors.fatturapa_import.max_files_exceeded',
        FILE_TOO_LARGE: 'errors.fatturapa_import.file_too_large',
        INVALID_FILE_TYPE: 'errors.fatturapa_import.invalid_file_type',
        P7M_EXTRACTION_FAILED: 'errors.fatturapa_import.p7m_extraction_failed',
    },
    // Referrers module
    REFERRERS: {
        NOT_FOUND: 'errors.referrers.not_found',
        INVALID_CODE: 'errors.referrers.invalid_code',
        CODE_EXPIRED: 'errors.referrers.code_expired',
        CODE_INACTIVE: 'errors.referrers.code_inactive',
        // Invito (o reinvio) dall'admin a un referrer disattivato: 409, lo stato
        // del profilo non lo consente.
        INVITATION_REFERRER_INACTIVE: 'errors.referrers.invitation_referrer_inactive',
        ALREADY_EXISTS: 'errors.referrers.already_exists',
        INVALID_HIERARCHY: 'errors.referrers.invalid_hierarchy',
        AGENT_REQUIRES_PARENT: 'errors.referrers.agent_requires_parent',
        CANNOT_DEACTIVATE_WITH_CHILDREN: 'errors.referrers.cannot_deactivate_with_children',
        INVITATION_NOT_FOUND: 'errors.referrers.invitation_not_found',
        INVITATION_EXPIRED: 'errors.referrers.invitation_expired',
        INVITATION_ALREADY_ACCEPTED: 'errors.referrers.invitation_already_accepted',
        EMAIL_ALREADY_USED: 'errors.referrers.email_already_used',
        EMAIL_REQUIRED: 'errors.referrers.email_required',
        CODE_ALREADY_EXISTS: 'errors.referrers.code_already_exists',
        USER_ROLE_CONFLICT: 'errors.referrers.user_role_conflict',
        PARENT_NOT_RESELLER: 'errors.referrers.parent_not_reseller',
        // Il cliente non è nella rete del referrer (scheda Clienti): 404.
        CLIENT_NOT_FOUND: 'errors.referrers.client_not_found',
        // Eccezione sulla quota del commerciale per un cliente il cui referente
        // non ha un commerciale (o non è un rivenditore): 400.
        CLIENT_WITHOUT_COMMERCIAL: 'errors.referrers.client_without_commercial',
        // Codice di logging: l'invito è salvato ma l'email non è partita.
        INVITATION_DELIVERY_FAILED: 'errors.referrers.invitation_delivery_failed',
        // Il tenant è già collegato a un altro referrer (Referrer.tenantId unico): 409.
        TENANT_ALREADY_LINKED: 'errors.referrers.tenant_already_linked',
        // Solo un rivenditore (RESELLER) ha un tenant proprio: 400.
        TENANT_REQUIRES_RESELLER: 'errors.referrers.tenant_requires_reseller',
        // Regola di sconto su un referrer che non è un rivenditore (AFFILIATE): 400.
        DISCOUNT_RULE_NOT_ALLOWED: 'errors.referrers.discount_rule_not_allowed',
    },
    // Commissions module
    COMMISSIONS: {
        NOT_FOUND: 'errors.commissions.not_found',
        ALREADY_APPROVED: 'errors.commissions.already_approved',
        ALREADY_PAID: 'errors.commissions.already_paid',
        ALREADY_CANCELLED: 'errors.commissions.already_cancelled',
        INVALID_STATUS_TRANSITION: 'errors.commissions.invalid_status_transition',
        CANNOT_MODIFY_PAID: 'errors.commissions.cannot_modify_paid',
        CANNOT_APPROVE_CANCELLED: 'errors.commissions.cannot_approve_cancelled',
        CANNOT_PAY_NOT_APPROVED: 'errors.commissions.cannot_pay_not_approved',
        // Export CSV oltre il tetto di righe: si restringe con filtri o tab.
        EXPORT_TOO_LARGE: 'errors.commissions.export_too_large',
        // Solo log: un incasso confermato su una fattura NURALE non ha prodotto
        // commissioni e richiede revisione (evidenza ambigua, vecchie righe…).
        RECEIPT_SYNC_FAILED: 'errors.commissions.receipt_sync_failed',
        // Solo log: una fattura NURALE di un contratto pagato dal rivenditore è
        // riconducibile a più tenant cliente; nessuna attribuzione, revisione.
        RECEIPT_ATTRIBUTION_AMBIGUOUS: 'errors.commissions.receipt_attribution_ambiguous',
    },
    // Debts module (DSCR - Loans/Financing)
    DEBTS: {
        NOT_FOUND: 'errors.debts.not_found',
        DOCUMENT_NOT_FOUND: 'errors.debts.document_not_found',
        COMPANY_NOT_FOUND: 'errors.debts.company_not_found',
        ACCOUNT_NOT_FOUND: 'errors.debts.account_not_found',
        INVALID_AMORTIZATION_MODE: 'errors.debts.invalid_amortization_mode',
        SCHEDULES_REQUIRED_FOR_MANUAL: 'errors.debts.schedules_required_for_manual',
        NUMBER_OF_PAYMENTS_REQUIRED_FOR_AUTO: 'errors.debts.number_of_payments_required_for_auto',
        INVALID_SCHEDULE_AMOUNTS: 'errors.debts.invalid_schedule_amounts',
        INVALID_DATE_RANGE: 'errors.debts.invalid_date_range',
        CANNOT_DELETE_WITH_PAYMENTS: 'errors.debts.cannot_delete_with_payments',
        CANNOT_DELETE_WITH_RECONCILIATION_LINKS: 'errors.debts.cannot_delete_with_reconciliation_links',
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.debts.export_format_not_supported',
        PAID_EXCEEDS_NEW_AMOUNT: 'errors.debts.paid_exceeds_new_amount',
        NO_REMAINING_PAYMENTS: 'errors.debts.no_remaining_payments',
    },
    // DSCR module (Debt Service Coverage Ratio)
    DSCR: {
        CALCULATION_FAILED: 'errors.dscr.calculation_failed',
        NO_DEBTS_FOUND: 'errors.dscr.no_debts_found',
        INVALID_HORIZON: 'errors.dscr.invalid_horizon',
        SNAPSHOT_NOT_FOUND: 'errors.dscr.snapshot_not_found',
        REPORT_GENERATION_FAILED: 'errors.dscr.report_generation_failed',
    },
    // Cash Flow Forecast module
    CASH_FLOW_FORECAST: {
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.cash_flow_forecast.export_format_not_supported',
        DAILY_RANGE_INVALID: 'errors.cash_flow_forecast.daily_range_invalid',
        DAILY_RANGE_TOO_LONG: 'errors.cash_flow_forecast.daily_range_too_long',
    },
    // AI Token Usage module
    AI: {
        QUOTA_EXCEEDED: 'errors.ai.quota_exceeded',
        QUOTA_NOT_CONFIGURED: 'errors.ai.quota_not_configured',
        QUOTA_ALREADY_EXISTS: 'errors.ai.quota_already_exists',
        HISTORY_NOT_FOUND: 'errors.ai.history_not_found',
    },
    // Onboarding module
    ONBOARDING: {
        ACCOUNT_NOT_FOUND: 'errors.onboarding.account_not_found',
        ACCOUNT_ALREADY_COMPLETED: 'errors.onboarding.account_already_completed',
        SCHEDULE_NOT_FOUND: 'errors.onboarding.schedule_not_found',
        SCHEDULE_ALREADY_RECONCILED: 'errors.onboarding.schedule_already_reconciled',
        SCHEDULE_NOT_SKIPPED: 'errors.onboarding.schedule_not_skipped',
        INVALID_CUTOFF_DATE: 'errors.onboarding.invalid_cutoff_date',
    },
    // Internal Transfers module (Giroconti)
    INTERNAL_TRANSFERS: {
        PAYPAL_FUNDING_REVIEW_REQUIRED: 'errors.internal_transfers.paypal_funding_review_required',
        NOT_FOUND: 'errors.internal_transfers.not_found',
        SOURCE_MOVEMENT_NOT_FOUND: 'errors.internal_transfers.source_movement_not_found',
        ALREADY_LINKED: 'errors.internal_transfers.already_linked',
        INVALID_TARGET_ACCOUNTS: 'errors.internal_transfers.invalid_target_accounts',
        INVALID_TRANSACTION_SELECTION: 'errors.internal_transfers.invalid_transaction_selection',
        PREPAID_CREDIT_ALREADY_IMPORTED: 'errors.internal_transfers.prepaid_credit_already_imported',
        // Suggestion errors
        SUGGESTION_NOT_FOUND: 'errors.internal_transfers.suggestion_not_found',
        SUGGESTION_ALREADY_PROCESSED: 'errors.internal_transfers.suggestion_already_processed',
        // Logging code: one card suggestion could not be refreshed after a card import.
        REFRESH_SUGGESTION_FAILED: 'errors.internal_transfers.refresh_suggestion_failed',
    },
    // Bank-to-bank transfers. Credit-card settlements use INTERNAL_TRANSFERS.
    ACCOUNT_TRANSFERS: {
        NOT_FOUND: 'errors.account_transfers.not_found',
        MOVEMENT_NOT_FOUND: 'errors.account_transfers.movement_not_found',
        INVALID_SOURCE: 'errors.account_transfers.invalid_source',
        INVALID_DESTINATION: 'errors.account_transfers.invalid_destination',
        SAME_ACCOUNT: 'errors.account_transfers.same_account',
        AMOUNT_MISMATCH: 'errors.account_transfers.amount_mismatch',
        MOVEMENT_ALREADY_LINKED: 'errors.account_transfers.movement_already_linked',
        TECHNICAL_MOVEMENT: 'errors.account_transfers.technical_movement',
        RECONCILED_MOVEMENT: 'errors.account_transfers.reconciled_movement',
        CANNOT_DELETE_LINKED_MOVEMENT: 'errors.account_transfers.cannot_delete_linked_movement',
        SUGGESTION_NOT_FOUND: 'errors.account_transfers.suggestion_not_found',
        MOVEMENT_LOCKED: 'errors.account_transfers.movement_locked',
        FEE_MISMATCH: 'errors.account_transfers.fee_mismatch',
        FEE_MOVEMENT_HAS_PARTS: 'errors.account_transfers.fee_movement_has_parts',
        FEE_CATEGORY_MISSING: 'errors.account_transfers.fee_category_missing',
        NOT_PENDING_COUNTERPART: 'errors.account_transfers.not_pending_counterpart',
        NOT_CLOSED: 'errors.account_transfers.not_closed',
        CONVERSION_INVALID: 'errors.account_transfers.conversion_invalid',
        INVALID_TARGET_TYPE: 'errors.account_transfers.invalid_target_type',
    },
    // Unione di conti duplicati (docs/PAYPAL_INTEGRATION.md §11)
    ACCOUNT_MERGE: {
        SAME_ACCOUNT: 'errors.account_merge.same_account',
        ALREADY_DECIDED: 'errors.account_merge.already_decided',
        NOT_MERGEABLE: 'errors.account_merge.not_mergeable',
        PAIR_INVALID: 'errors.account_merge.pair_invalid',
        CONFLICT_UNRESOLVED: 'errors.account_merge.conflict_unresolved',
    },
    // Import dell'export "Attività" di PayPal Business
    PAYPAL_IMPORT: {
        FORMAT_INVALID: 'errors.paypal_import.format_invalid',
        BALANCE_CHAIN_BROKEN: 'errors.paypal_import.balance_chain_broken',
        CURRENCY_ACCOUNT_MISSING: 'errors.paypal_import.currency_account_missing',
        ACCOUNT_NOT_PAYPAL: 'errors.paypal_import.account_not_paypal',
        NO_MOVEMENTS: 'errors.paypal_import.no_movements',
        NO_FILE_PROVIDED: 'errors.paypal_import.no_file_provided',
        FILE_TOO_LARGE: 'errors.paypal_import.file_too_large',
        INVALID_FILE_TYPE: 'errors.paypal_import.invalid_file_type',
    },
    // Distinte RIBA module
    DISTINTE_RIBA: {
        NOT_FOUND: 'errors.distinte_riba.not_found',
        INVALID_STATUS_TRANSITION: 'errors.distinte_riba.invalid_status_transition',
        MISSING_CREDITOR_IBAN: 'errors.distinte_riba.missing_creditor_iban',
        MISSING_DEBTOR_IBAN: 'errors.distinte_riba.missing_debtor_iban',
        MISSING_SIA_CODE: 'errors.distinte_riba.missing_sia_code',
        INVALID_DOWNLOAD_FORMAT: 'errors.distinte_riba.invalid_download_format',
        FORMAT_NOT_AVAILABLE_FOR_TYPE: 'errors.distinte_riba.format_not_available_for_type',
        DISTINTA_REQUIRED_FOR_ADVANCE: 'errors.distinte_riba.distinta_required_for_advance',
        INVALID_AMOUNT_RANGE: 'errors.distinte_riba.invalid_amount_range',
        EXPORT_FORMAT_NOT_SUPPORTED: 'errors.distinte_riba.export_format_not_supported',
        BULK_DOWNLOAD_FAILED: 'errors.distinte_riba.bulk_download_failed',
        /** Codice di log: accettazione in blocco di un movimento RiBa fallita in modo inatteso. */
        SCAN_ACCEPT_FAILED: 'errors.distinte_riba.scan_accept_failed',
        ADVANCE_OVERRIDE_SCHEDULE_NOT_INCLUDED: 'errors.distinte_riba.advance_override_schedule_not_included',
        ADVANCE_BASE_EXCEEDS_MAXIMUM: 'errors.distinte_riba.advance_base_exceeds_maximum',
    },
    // Integrations - base comune connettori esterni (app-connector layer)
    INTEGRATIONS: {
        FEATURE_DISABLED: 'errors.integrations.feature_disabled',
        PROVIDER_NOT_REGISTERED: 'errors.integrations.provider_not_registered',
        NOT_CONNECTED: 'errors.integrations.not_connected',
        ALREADY_CONNECTED: 'errors.integrations.already_connected',
        SYNC_FAILED: 'errors.integrations.sync_failed',
        SYNC_IN_PROGRESS: 'errors.integrations.sync_in_progress',
        SYNC_NOT_SUPPORTED: 'errors.integrations.sync_not_supported',
        INVALID_CREDENTIALS: 'errors.integrations.invalid_credentials',
        WEBHOOK_SIGNATURE_INVALID: 'errors.integrations.webhook_signature_invalid',
        WEBHOOK_NOT_SUPPORTED: 'errors.integrations.webhook_not_supported',
        WEBHOOK_INVALID_PAYLOAD: 'errors.integrations.webhook_invalid_payload',
        MANUAL_LINK_CONFLICT: 'errors.integrations.manual_link_conflict',
        CONNECT_VIA_DEDICATED_ENDPOINT: 'errors.integrations.connect_via_dedicated_endpoint',
        // errorCode per il logging persistito (cron sweeper delle integrazioni)
        SCHEDULED_SYNC_FAILED: 'errors.integrations.scheduled_sync_failed',
    },
    // Layer ERP generico — errorCode per il logging persistito (nessun messaggio
    // utente: questi codici finiscono in DB, non in una traduzione)
    ERP: {
        SYNC_FAILED: 'errors.erp.sync_failed',
        PUSH_FAILED: 'errors.erp.push_failed',
    },
    // Jet HR connector module
    JET_HR: {
        NOT_CONNECTED: 'errors.jet_hr.not_connected',
        ALREADY_CONNECTED: 'errors.jet_hr.already_connected',
        INVALID_API_KEY: 'errors.jet_hr.invalid_api_key',
        SYNC_FAILED: 'errors.jet_hr.sync_failed',
        SYNC_IN_PROGRESS: 'errors.jet_hr.sync_in_progress',
        API_UNREACHABLE: 'errors.jet_hr.api_unreachable',
    },
    // PayPal connector module (collegamento diretto alle API, sola lettura)
    PAYPAL: {
        INVALID_CREDENTIALS: 'errors.paypal.invalid_credentials',
        TRANSACTION_SEARCH_NOT_ENABLED: 'errors.paypal.transaction_search_not_enabled',
        CONNECTION_NOT_FOUND: 'errors.paypal.connection_not_found',
        NOT_CONNECTED: 'errors.paypal.not_connected',
        SYNC_IN_PROGRESS: 'errors.paypal.sync_in_progress',
        PROVIDER_UNAVAILABLE: 'errors.paypal.provider_unavailable',
        BALANCES_UNAVAILABLE: 'errors.paypal.balances_unavailable',
        // Chiusura storica non ancora pubblicata da PayPal (ritardo anche oltre 3 ore)
        BALANCES_NOT_PUBLISHED: 'errors.paypal.balances_not_published',
        SANDBOX_NOT_ALLOWED: 'errors.paypal.sandbox_not_allowed',
        ACCOUNT_LINKED_TO_CONNECTION: 'errors.paypal.account_linked_to_connection',
        INVALID_DATE_RANGE: 'errors.paypal.invalid_date_range',
        RATE_LIMITED: 'errors.paypal.rate_limited',
        BAD_REQUEST: 'errors.paypal.bad_request',
        READ_ONLY_CONNECTOR: 'errors.paypal.read_only_connector',
        // Validazione per campo di ConnectPaypalDto
        INVALID_CLIENT_ID_FORMAT: 'errors.paypal.invalid_client_id_format',
        INVALID_CLIENT_SECRET_FORMAT: 'errors.paypal.invalid_client_secret_format',
        // errorCode per il logging persistito (nessun testo utente)
        API_ERROR: 'errors.paypal.api_error',
        SYNC_FAILED: 'errors.paypal.sync_failed',
        UNKNOWN_EVENT_CODE: 'errors.paypal.unknown_event_code',
    },
    // Reviso connector module (layer ERP)
    REVISO: {
        NOT_CONNECTED: 'errors.reviso.not_connected',
        INVALID_TOKENS: 'errors.reviso.invalid_tokens',
        CONNECT_URL_NOT_CONFIGURED: 'errors.reviso.connect_url_not_configured',
        SYNC_FAILED: 'errors.reviso.sync_failed',
        API_UNREACHABLE: 'errors.reviso.api_unreachable',
        PUSH_NOT_ENABLED: 'errors.reviso.push_not_enabled',
        PARTIAL_REQUIRES_INBOUND: 'errors.reviso.partial_requires_inbound',
        // errorCode per il logging persistito (cron scadenza grant token)
        CREDENTIALS_CHECK_FAILED: 'errors.reviso.credentials_check_failed',
        CREDENTIALS_EMAIL_FAILED: 'errors.reviso.credentials_email_failed',
        // errorCode per il logging persistito (errori HTTP dell'API Reviso)
        API_ERROR: 'errors.reviso.api_error',
    },
    // Genya connector module (scadenzario xlsx, layer ERP)
    GENYA: {
        NOT_CONNECTED: 'errors.genya.not_connected',
        INVALID_CONTENT_TYPE: 'errors.genya.invalid_content_type',
        NO_FILE_PROVIDED: 'errors.genya.no_file_provided',
        INVALID_FILE_TYPE: 'errors.genya.invalid_file_type',
        MULTIPLE_FILES: 'errors.genya.multiple_files',
        FILE_TOO_LARGE: 'errors.genya.file_too_large',
        INVALID_SPREADSHEET: 'errors.genya.invalid_spreadsheet',
        READER_BUSY: 'errors.genya.reader_busy',
        MISSING_COLUMNS: 'errors.genya.missing_columns',
        IMPORT_FAILED: 'errors.genya.import_failed',
        // Anomalie NON bloccanti dell'import: una riga di error log per valore
        // distinto e per import, così una nuova etichetta di Genya o un export
        // che taglia le rate si vedono dal backoffice invece di restare nel
        // report di chi ha caricato il file.
        UNKNOWN_PAYMENT_TYPE: 'errors.genya.unknown_payment_type',
        MISSING_INSTALLMENTS: 'errors.genya.missing_installments',
        // Ritenuta d'acconto con aliquota non ricostruibile in modo univoco: il
        // documento si crea con l'importo esatto del file e l'aliquota standard
        // del lavoro autonomo. Va contato — se comparisse spesso vorrebbe dire
        // che l'insieme di aliquote del solutore è da allargare.
        WITHHOLDING_RATE_ASSUMED: 'errors.genya.withholding_rate_assumed',
    },
    // Payroll Import module
    PAYROLL_IMPORT: {
        INVALID_FILE_TYPE: 'errors.payroll_import.invalid_file_type',
        FILE_TOO_LARGE: 'errors.payroll_import.file_too_large',
        INVALID_XML_FORMAT: 'errors.payroll_import.invalid_xml_format',
        NOT_CBI_FORMAT: 'errors.payroll_import.not_cbi_format',
        NO_TRANSACTIONS: 'errors.payroll_import.no_transactions',
        NO_FILE_PROVIDED: 'errors.payroll_import.no_file_provided',
        INVALID_PDF: 'errors.payroll_import.invalid_pdf',
        NO_PAYSLIPS_FOUND: 'errors.payroll_import.no_payslips_found',
        EXTRACTION_FAILED: 'errors.payroll_import.extraction_failed',
        TOO_MANY_PAGES: 'errors.payroll_import.too_many_pages',
        TAX_CODE_NOT_EMPLOYEE: 'errors.payroll_import.tax_code_not_employee',
        MULTIPLE_FILES: 'errors.payroll_import.multiple_files',
    },
    BANK_IMPORT: {
        INVALID_FILE_TYPE: 'errors.bank_import.invalid_file_type',
        INVALID_SPREADSHEET: 'errors.bank_import.invalid_spreadsheet',
    },
    // PDF Import module (bank statements + card statements)
    PDF_IMPORT: {
        PROVIDER_TIMED_OUT: 'errors.pdf_import.provider_timed_out',
        EXTRACTION_INCOMPLETE: 'errors.pdf_import.extraction_incomplete',
        EXTRACTION_UNAVAILABLE: 'errors.pdf_import.extraction_unavailable',
        ONLY_PDF_ALLOWED: 'errors.pdf_import.only_pdf_allowed',
        FILE_TOO_LARGE: 'errors.pdf_import.file_too_large',
        NO_FILES_PROVIDED: 'errors.pdf_import.no_files_provided',
        NO_DATA_PROVIDED: 'errors.pdf_import.no_data_provided',
        EXTRACTION_FAILED: 'errors.pdf_import.extraction_failed',
        EXTRACTION_TIMED_OUT: 'errors.pdf_import.extraction_timed_out',
    },
    // Table Preferences module
    TABLE_PREFERENCES: {
        INVALID_TABLE_KEY: 'errors.table_preferences.invalid_table_key',
    },
    // Subscription Notifications module
    SUBSCRIPTION_NOTIFICATIONS: {
        TENANT_NOT_FOUND: 'errors.subscription_notifications.tenant_not_found',
        TENANT_NO_EMAIL: 'errors.subscription_notifications.tenant_no_email',
        SEND_FAILED: 'errors.subscription_notifications.send_failed',
    },
    // Subscriptions module (disdetta/riattivazione dal backoffice)
    SUBSCRIPTIONS: {
        NO_ACTIVE_SUBSCRIPTION: 'errors.subscriptions.no_active_subscription',
        ALREADY_CANCELLED: 'errors.subscriptions.already_cancelled',
        NOT_CANCELLED: 'errors.subscriptions.not_cancelled',
        PERIOD_ALREADY_PAID: 'errors.subscriptions.period_already_paid',
        // Race con il motore di rinnovo: il periodo letto e` stato chiuso/rinnovato
        // tra la lettura e la scrittura condizionata, oltre il tetto di retry.
        CONCURRENT_MODIFICATION: 'errors.subscriptions.concurrent_modification',
        INVOICE_PAYMENT_UNMATCHED: 'errors.subscriptions.invoice_payment_unmatched',
        // Guasto del collegamento automatico fattura NURALE → periodo pagato.
        INVOICE_PAYMENT_SYNC_FAILED: 'errors.subscriptions.invoice_payment_sync_failed',
        INSTALLMENT_NOT_FOUND: 'errors.subscriptions.installment_not_found',
        NOTHING_TO_WAIVE: 'errors.subscriptions.nothing_to_waive',
        BILLING_SYNC_FAILED: 'errors.subscriptions.billing_sync_failed',
        PREPAYMENT_PERIOD_INVALID: 'errors.subscriptions.prepayment_period_invalid',
        PREPAYMENT_ALREADY_RECORDED: 'errors.subscriptions.prepayment_already_recorded',
        // Nessun prossimo periodo da incassare: senza rinnovo o disdetto.
        PREPAYMENT_NOT_RENEWING: 'errors.subscriptions.prepayment_not_renewing',
        // Il tenant paga le fatture NURALE (PVX), non i periodi di abbonamento.
        PREPAYMENT_BILLED_BY_NURALE: 'errors.subscriptions.prepayment_billed_by_nurale',
        // Il periodo è già scaduto: il rinnovo non si cambia più, si riattiva.
        PERIOD_EXPIRED: 'errors.subscriptions.period_expired',
        // Codici di log del ricalcolo del blocco per mancato pagamento.
        PAYMENT_BLOCK_RECOMPUTE_FAILED: 'errors.subscriptions.payment_block_recompute_failed',
        PAYMENT_BLOCK_FROM_INVALID: 'errors.subscriptions.payment_block_from_invalid',
        // Codici di log degli avvisi di pagamento e di mancato rinnovo.
        PAYMENT_REMINDER_FAILED: 'errors.subscriptions.payment_reminder_failed',
        // Contratto pagato dal rivenditore: il cliente non vede prezzi né pagamenti.
        BILLING_MANAGED_BY_RESELLER: 'errors.subscriptions.billing_managed_by_reseller',
        PAYMENT_REMINDER_EMAIL_FAILED: 'errors.subscriptions.payment_reminder_email_failed',
    },
    // Admin Notifications module (Backoffice)
    ADMIN_NOTIFICATIONS: {
        NOT_FOUND: 'errors.admin_notifications.not_found',
        ALREADY_SENT: 'errors.admin_notifications.already_sent',
        CANNOT_MODIFY: 'errors.admin_notifications.cannot_modify',
        SCHEDULED_IN_PAST: 'errors.admin_notifications.scheduled_in_past',
        NO_ACTIVE_TENANTS: 'errors.admin_notifications.no_active_tenants',
    },
    // Gestify module (external integration)
    GESTIFY: {
        TENANT_NOT_FOUND: 'errors.gestify.tenant_not_found',
        INVALID_API_KEY: 'errors.gestify.invalid_api_key',
        MISSING_API_KEY: 'errors.gestify.missing_api_key',
        NOT_CONFIGURED: 'errors.gestify.not_configured',
    },
    // Support tickets (Gestify Lambda integration)
    SUPPORT_TICKET: {
        CREATION_FAILED: 'errors.support_ticket.creation_failed',
        CONTRACT_NOT_FOUND: 'errors.support_ticket.contract_not_found',
        INVALID_VAT: 'errors.support_ticket.invalid_vat',
        INVALID_INPUT: 'errors.support_ticket.invalid_input',
        EXTERNAL_SERVICE_UNAVAILABLE: 'errors.support_ticket.external_service_unavailable',
        TENANT_VAT_MISSING: 'errors.support_ticket.tenant_vat_missing',
        SLOT_FETCH_FAILED: 'errors.support_ticket.slot_fetch_failed',
        ATTACHMENT_UPLOAD_FAILED: 'errors.support_ticket.attachment_upload_failed',
        SCHEDULE_REQUIRED_FOR_TRAINING: 'errors.support_ticket.schedule_required_for_training',
        // Helpdesk Previxio (ticket persistiti). Le chiavi sopra restano per il
        // codice Tecnos/Gestify, tuttora commentato ma non rimosso.
        NOT_FOUND: 'errors.support_ticket.not_found',
        MESSAGE_NOT_FOUND: 'errors.support_ticket.message_not_found',
        ATTACHMENT_NOT_FOUND: 'errors.support_ticket.attachment_not_found',
        FORBIDDEN: 'errors.support_ticket.forbidden',
        INVALID_STATUS_TRANSITION: 'errors.support_ticket.invalid_status_transition',
        REOPEN_WINDOW_EXPIRED: 'errors.support_ticket.reopen_window_expired',
        ASSIGNEE_NOT_OPERATOR: 'errors.support_ticket.assignee_not_operator',
        EMPTY_MESSAGE: 'errors.support_ticket.empty_message',
        MAX_ATTACHMENTS_EXCEEDED: 'errors.support_ticket.max_attachments_exceeded',
        ATTACHMENT_TOO_LARGE: 'errors.support_ticket.attachment_too_large',
        ATTACHMENT_INVALID_MIME_TYPE: 'errors.support_ticket.attachment_invalid_mime_type',
        STORAGE_UNAVAILABLE: 'errors.support_ticket.storage_unavailable',
        // Codici di logging delle email agli operatori assegnatari: l'invio non
        // blocca mai l'operazione, ma il backoffice li traduce come le altre
        // chiavi del gruppo (support-ticket-errors.test.ts ne verifica la copertura).
        ASSIGNMENT_EMAIL_FAILED: 'errors.support_ticket.assignment_email_failed',
        ASSIGNEE_REPLY_ALERT_FAILED: 'errors.support_ticket.assignee_reply_alert_failed',
    },
    // MCP - Model Context Protocol (OAuth 2.1 + DCR)
    MCP: {
        // Bearer / token validation
        MISSING_TOKEN: 'errors.mcp.missing_token',
        INVALID_TOKEN: 'errors.mcp.invalid_token',
        TOKEN_EXPIRED: 'errors.mcp.token_expired',
        TOKEN_REVOKED: 'errors.mcp.token_revoked',
        // OAuth flow
        INVALID_CLIENT: 'errors.mcp.invalid_client',
        INVALID_GRANT: 'errors.mcp.invalid_grant',
        INVALID_REQUEST: 'errors.mcp.invalid_request',
        INVALID_REDIRECT_URI: 'errors.mcp.invalid_redirect_uri',
        INVALID_PKCE: 'errors.mcp.invalid_pkce',
        UNSUPPORTED_GRANT_TYPE: 'errors.mcp.unsupported_grant_type',
        UNSUPPORTED_RESPONSE_TYPE: 'errors.mcp.unsupported_response_type',
        CODE_EXPIRED: 'errors.mcp.code_expired',
        CODE_ALREADY_CONSUMED: 'errors.mcp.code_already_consumed',
        CONNECTION_NOT_FOUND: 'errors.mcp.connection_not_found',
        INVALID_TENANT: 'errors.mcp.invalid_tenant',
        INVALID_AUDIENCE: 'errors.mcp.invalid_audience',
        LOGO_PROXY_FAILED: 'errors.mcp.logo_proxy_failed',
        LOGO_NOT_FOUND: 'errors.mcp.logo_not_found',
        INVALID_ORIGIN: 'errors.mcp.invalid_origin',
        // Runtime
        RATE_LIMITED: 'errors.mcp.rate_limited',
        INSUFFICIENT_PERMISSIONS: 'errors.mcp.insufficient_permissions',
        TOOL_NOT_FOUND: 'errors.mcp.tool_not_found',
        TENANT_USER_INACTIVE: 'errors.mcp.tenant_user_inactive',
        CONNECT_PERMISSION_REQUIRED: 'errors.mcp.connect_permission_required',
        CLIENT_NOT_ALLOWED: 'errors.mcp.client_not_allowed',
        MISCONFIGURED: 'errors.mcp.misconfigured',
    },
    // Document attachments module (allegati file su GCS)
    DOCUMENT_ATTACHMENTS: {
        NOT_FOUND: 'errors.document_attachments.not_found',
        DOCUMENT_NOT_FOUND: 'errors.document_attachments.document_not_found',
        INVALID_CONTENT_TYPE: 'errors.document_attachments.invalid_content_type',
        NO_FILE_PROVIDED: 'errors.document_attachments.no_file_provided',
        FILE_TOO_LARGE: 'errors.document_attachments.file_too_large',
        INVALID_MIME_TYPE: 'errors.document_attachments.invalid_mime_type',
        MAX_FILES_PER_DOCUMENT_EXCEEDED: 'errors.document_attachments.max_files_per_document_exceeded',
        STORAGE_UNAVAILABLE: 'errors.document_attachments.storage_unavailable',
        STORAGE_UPLOAD_FAILED: 'errors.document_attachments.storage_upload_failed',
        STORAGE_DOWNLOAD_FAILED: 'errors.document_attachments.storage_download_failed',
        STORAGE_DELETE_FAILED: 'errors.document_attachments.storage_delete_failed',
    },
    // Validation errors (from class-validator)
    VALIDATION: {
        FIELD_REQUIRED: 'errors.validation.field_required',
        INVALID_EMAIL: 'errors.validation.invalid_email',
        INVALID_PHONE: 'errors.validation.invalid_phone',
        INVALID_UUID: 'errors.validation.invalid_uuid',
        MUST_BE_NUMBER: 'errors.validation.must_be_number',
        MUST_BE_POSITIVE: 'errors.validation.must_be_positive',
        MUST_BE_INTEGER: 'errors.validation.must_be_integer',
        MAX_LENGTH: 'errors.validation.max_length',
        MIN_LENGTH: 'errors.validation.min_length',
        MIN_VALUE: 'errors.validation.min_value',
        MAX_VALUE: 'errors.validation.max_value',
        INVALID_DATE: 'errors.validation.invalid_date',
        INVALID_ENUM: 'errors.validation.invalid_enum',
    },
    // Prisma errors (database errors)
    PRISMA: {
        RECORD_NOT_FOUND: 'errors.prisma.record_not_found', // P2025
        UNIQUE_CONSTRAINT: 'errors.prisma.unique_constraint', // P2002
        FOREIGN_KEY_CONSTRAINT: 'errors.prisma.foreign_key_constraint', // P2003
        RECORD_IN_USE: 'errors.prisma.record_in_use', // P2014
    },
    // Product releases (registro release + comunicazioni Slack dal backoffice)
    PRODUCT_RELEASES: {
        NO_CUSTOMER_CHANGES: 'errors.product_releases.no_customer_changes',
        SLACK_SEND_IN_PROGRESS: 'errors.product_releases.slack_send_in_progress',
    },
    // Electronic invoice XML (persistenza XML FatturaPA fatture di vendita)
    ELECTRONIC_XML: {
        FETCH_FAILED: 'errors.electronic_xml.fetch_failed', // download XML da ACube fallito
    },
    /**
     * Controlli automatici di salute (`docs/TENANT_HEALTH.md`). Codici SOLO da
     * registro errori: non sono eccezioni, non arrivano mai all'utente, quindi
     * non hanno traduzione. UNA chiave per CHECK, non per finding: tutti i
     * finding non-OK dello stesso controllo si aggregano sotto lo stesso codice.
     */
    TENANT_HEALTH: {
        /** Il giro giornaliero non è arrivato in fondo (guasto del job, non di un check). */
        RUN_FAILED: 'errors.tenant_health.run_failed',
        QUEUE: 'errors.tenant_health.queue',
        /** Lo scarico automatico del cassetto fiscale su ACube. */
        FISCAL_DRAWER_SCHEDULE: 'errors.tenant_health.fiscal_drawer_schedule',
        /** I job giornalieri dello scarico (regola dei 3 giorni). */
        FISCAL_DRAWER_DAILY_JOBS: 'errors.tenant_health.fiscal_drawer_daily_jobs',
        /** Lo scarico F24 dal cassetto su ACube, confrontato con il contratto. */
        FISCAL_DRAWER_F24_SCHEDULE: 'errors.tenant_health.fiscal_drawer_f24_schedule',
        /** I job giornalieri dello scarico F24 (stessa regola dei 3 giorni). */
        FISCAL_DRAWER_F24_DAILY_JOBS: 'errors.tenant_health.fiscal_drawer_f24_daily_jobs',
        /** I webhook `job`/`job-error` di ACube arrivano davvero. */
        FISCAL_DRAWER_WEBHOOK_JOBS: 'errors.tenant_health.fiscal_drawer_webhook_jobs',
        /** Fatture presenti su ACube e assenti da noi. */
        FISCAL_DRAWER_INVOICE_COUNT: 'errors.tenant_health.fiscal_drawer_invoice_count',
        FISCAL_DRAWER_INVOICE_SEQUENCE: 'errors.tenant_health.fiscal_drawer_invoice_sequence',
        INVOICE_RECOVERY_EVALUATION_FAILED: 'errors.tenant_health.invoice_recovery_evaluation_failed',
        INVOICE_RECOVERY_SUBMISSION_UNCERTAIN: 'errors.tenant_health.invoice_recovery_submission_uncertain',
    },
    /**
     * Notifiche push verso l'app (Firebase Cloud Messaging). Codici SOLO da
     * registro errori: l'invio è best-effort e non arriva mai all'utente come
     * eccezione, quindi non hanno traduzione.
     */
    PUSH_NOTIFICATIONS: {
        /** FCM ha rifiutato o non ha risposto a un invio (token valido). */
        FCM_SEND_FAILED: 'errors.push_notifications.fcm_send_failed',
        /** APNs ha rifiutato o non ha accettato un invio (token valido). */
        APNS_SEND_FAILED: 'errors.push_notifications.apns_send_failed',
        /** Guasto inatteso nel preparare o consegnare una push. */
        DELIVERY_FAILED: 'errors.push_notifications.delivery_failed',
    },
};
//# sourceMappingURL=error-keys.js.map